import { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  WeeklySummary,
  GoalStreak,
  CrewMessageBanner,
  formatDateUS,
} from "./DashboardFeatures";
import { getCheckpointLabels } from "../lib/checkpointLabels";
import QuickJumpDock from "../components/QuickJumpDock";
import {
  Users,
  ChartLine,
  ChartLineUp,
  Bell,
  Plus,
  Check,
  Warning,
  Envelope,
  Target,
  Clock,
  PencilSimple,
  X,
  SignOut,
  Lightning,
  UserMinus,
  UserPlus,
  Trash,
  Lock,
  TrendUp,
  Trophy,
  EnvelopeSimple,
  Gear,
  ClockClockwise,
  CaretUp,
  CaretDown,
  DotsSixVertical,
  House,
  Crown,
  Medal,
  Key,
  CalendarBlank,
  ArrowsLeftRight,
  ListChecks,
  Prohibit,
  ShieldCheck,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import EmailCaptureGlanceCard from "../components/EmailCaptureGlanceCard";
import WinTheDayHero from "../components/manager_dashboard/WinTheDayHero";
import PaceToWinCard from "../components/manager_dashboard/PaceToWinCard";
import TodaysGamePlanStrip from "../components/manager_dashboard/TodaysGamePlanStrip";
import ManagerHandoffCard from "../components/manager_dashboard/ManagerHandoffCard";
import NeedsAttentionStrip from "../components/manager_dashboard/NeedsAttentionStrip";
import WhosOnTheFloor from "../components/manager_dashboard/WhosOnTheFloor";
import WtdPendingVerificationTray from "../components/manager_dashboard/WtdPendingVerificationTray";
import FreightProgressCard from "../components/manager_dashboard/FreightProgressCard";
import EmptyStateChip from "../components/manager_dashboard/EmptyStateChip";
import WhatsFreshTodayStrip from "../components/manager_dashboard/WhatsFreshTodayStrip";
import TruckPlanCard from "../components/manager_dashboard/TruckPlanCard";
import { Input } from "../components/ui/input";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import Banner from "../components/Banner";
import EyesOnBizPreview from "../components/EyesOnBizPreview";
import CoverageTasksLiveFeed from "../components/CoverageTasksLiveFeed";
import WeeklyFiguresCard from "../components/WeeklyFiguresCard";
import ContestsAdmin from "./ContestsAdmin";
// BalloonLeaderboard import removed 2026-05-19 with the Heart Balloons cleanup.
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STORE_TIMEZONE = "America/Denver";

const getTodayDate = () => {
  return new Date().toLocaleDateString("en-CA", { timeZone: STORE_TIMEZONE });
};

// ---- Date Picker Strip ----------------------------------------------------
// Beautiful single-tap calendar so managers can back-fill data for previous
// days without fighting an ugly read-only text field. Quick shortcuts for
// "Today" and "Yesterday" (covers the 90% case) sit next to the main button;
// tapping the main button opens the shadcn Calendar in a popover so they can
// reach any date in <2 taps. (user fix 2026-02-22 "mission impossible")
function DatePickerStrip({ selectedDate, onChange }) {
  const [open, setOpen] = useState(false);
  // Convert YYYY-MM-DD → Date (parse-as-local, NOT UTC, to avoid the
  // off-by-one we always hit when JS pulls midnight UTC into MST).
  const toLocalDate = (ymd) => {
    if (!ymd) return new Date();
    const [y, m, d] = ymd.split("-").map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
  };
  const toYMD = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const todayYmd = getTodayDate();
  const yesterday = (() => {
    const t = toLocalDate(todayYmd);
    t.setDate(t.getDate() - 1);
    return toYMD(t);
  })();
  const isToday = selectedDate === todayYmd;
  const isYesterday = selectedDate === yesterday;
  const dateObj = toLocalDate(selectedDate);
  // Pretty label: "Today · Tue, Feb 22"  /  "Yesterday · Mon, Feb 21"  /  "Sat, Feb 19"
  const friendly = dateObj.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: STORE_TIMEZONE,
  });
  const headline = isToday ? "Today" : isYesterday ? "Yesterday" : friendly;
  const subline = isToday || isYesterday ? friendly : null;

  return (
    <div className="flex items-center gap-2 min-w-0" data-testid="date-picker-strip">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={`flex items-center gap-2.5 h-10 pl-3 pr-2 rounded-xl border transition-all ${
              isToday
                ? "border-blue-200 bg-blue-50 hover:bg-blue-100"
                : "border-amber-300 bg-amber-50 hover:bg-amber-100"
            }`}
            data-testid="date-selector"
          >
            <CalendarBlank weight="bold" className={`w-4 h-4 ${isToday ? "text-blue-600" : "text-amber-700"}`} />
            <div className="text-left min-w-0">
              <p className={`text-sm font-bold leading-tight ${isToday ? "text-blue-900" : "text-amber-900"}`}>{headline}</p>
              {subline && <p className="text-[10px] text-slate-500 leading-tight">{subline}</p>}
            </div>
            <CaretDown weight="bold" className={`w-3 h-3 ml-0.5 ${isToday ? "text-blue-500" : "text-amber-600"}`} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2" align="start">
          <div className="flex gap-1.5 mb-2">
            <button
              onClick={() => { onChange(todayYmd); setOpen(false); }}
              className="flex-1 h-8 rounded-md text-xs font-bold bg-blue-600 text-white hover:bg-blue-700"
              data-testid="date-shortcut-today"
            >
              Today
            </button>
            <button
              onClick={() => { onChange(yesterday); setOpen(false); }}
              className="flex-1 h-8 rounded-md text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-700"
              data-testid="date-shortcut-yesterday"
            >
              Yesterday
            </button>
          </div>
          <Calendar
            mode="single"
            selected={dateObj}
            onSelect={(d) => {
              if (!d) return;
              onChange(toYMD(d));
              setOpen(false);
            }}
            // Block future dates — managers can only back-fill, never forward-fill.
            disabled={(d) => d > toLocalDate(todayYmd)}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {!isToday && (
        <button
          type="button"
          onClick={() => onChange(todayYmd)}
          className="text-[11px] font-bold uppercase tracking-wider text-blue-600 hover:underline px-1"
          data-testid="date-go-to-today"
        >
          ← Today
        </button>
      )}
    </div>
  );
}

// Helper: progress bar class based on rate
const getProgressClass = (atGoal, rate, threshold = 0.2) => {
  if (atGoal) return "at-goal";
  if (rate < threshold) return "critical";
  return "below-goal";
};

// Helper: rank badge class
const getRankBadgeClass = (idx) => {
  if (idx === 0) return "bg-amber-400 text-white";
  if (idx === 1) return "bg-slate-300 text-slate-700";
  if (idx === 2) return "bg-amber-700 text-white";
  return "bg-slate-100 text-slate-600";
};

// ---- Stat Card ----
const StatCard = ({ label, value, subValue, status }) => (
  <div className="dashboard-card">
    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">
      {label}
    </p>
    <p className="text-3xl font-bold" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
      {value}
    </p>
    {subValue && <p className="text-sm text-slate-500 mt-1">{subValue}</p>}
  </div>
);

// ---- Email Capture at a Glance ----
// Hero tile pulling the four numbers MODs care about most. Lives as a
// shared component so the Zone Sheet's Eyes on the Biz tile renders
// the exact same surface (Phase 2 of the Game Plan rollout —
// 2026-06-15). Implementation in /components/EmailCaptureGlanceCard.js.

// ---- Section Header (Dashboard reorganization 2026-06-14) ----
// Tiny, low-key label that introduces each functional block of the
// Manager Dashboard. Sized so it costs little vertical space on mobile
// while still being a clear visual divider between Alerts / Hero /
// Store Pulse / Game Plan / Manager Communication / Operations /
// Recognition & Performance. The optional `right` slot can carry
// an inline action (e.g., "Customize layout") without forcing a
// second row.
const SectionHeader = ({ title, right, testid }) => (
  // 2026-02 layout pass — dropped the `kicker` prop entirely (was
  // rendering "Step 1"/"Step 2" etc.). Step numbering wasn't tracking
  // an actual checklist state — pure visual noise ×6 = ~84px per
  // dashboard. Section-header top margin also tightened (mt-6 → mt-4)
  // and mb-2 → mb-1.5 for ~30% section-gap reduction across the
  // whole dashboard.
  <div
    className="flex items-end justify-between gap-3 mt-4 mb-1.5 px-0.5"
    data-testid={testid}
  >
    <div className="min-w-0">
      <h2
        className="text-sm sm:text-base font-black uppercase tracking-[0.14em] text-slate-800 leading-tight"
        style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
      >
        {title}
      </h2>
    </div>
    {right && <div className="shrink-0">{right}</div>}
  </div>
);

// ---- Goal Calculator ----
const GoalCalculator = ({ tallyData, settings }) => {
  if (!tallyData || !tallyData.store_transactions) {
    return (
      <div className="goal-calculator" data-testid="goal-calculator">
        <div className="flex items-center gap-2 mb-4">
          <Target weight="bold" className="w-5 h-5" />
          <span className="text-xs uppercase tracking-wider font-semibold text-slate-400">
            Goal Calculator
          </span>
        </div>
        <p className="text-slate-400">No transaction data yet. Enter a checkpoint reading.</p>
      </div>
    );
  }

  const rate = tallyData.capture_rate || 0;
  const goalRate = tallyData.goal_rate || 0.35;
  const stretchRate = tallyData.stretch_goal_rate || 0.50;
  // totalEmails / storeTx locals removed 2026-06-13 with the dedupe
  // pass — the Capture Rate big-number column they fed is now only
  // shown by the hero + EmailCaptureGlanceCard above this tile.
  const neededGoal = tallyData.emails_needed_goal || 0;
  const neededStretch = tallyData.emails_needed_stretch || 0;
  const emailsForGoal = tallyData.emails_for_goal || 0;
  const emailsForStretch = tallyData.emails_for_stretch || 0;
  const atGoal = tallyData.at_goal || false;
  const atStretch = tallyData.at_stretch || false;
  const latestCheckpoint = tallyData.latest_checkpoint;
  const checkpoints = tallyData.checkpoints || {};

  // Per-slot display labels driven by today's effective store hours.
  // Single source of truth lives in /lib/checkpointLabels.js so this
  // mapping stays in sync with the checkpoint tab strip in CheckpointEntry.
  const cpLabels = getCheckpointLabels(settings);

  return (
    <div className="goal-calculator" data-testid="goal-calculator">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-2xl text-white shadow-md shrink-0" style={{ background: "linear-gradient(135deg, #8b5cf6, #ec4899)" }}>
            <Target weight="fill" className="w-4 h-4" />
          </span>
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-black text-fuchsia-300 leading-none">
              Mission Control
            </p>
            <h3 className="text-lg font-black text-white leading-tight mt-1" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
              Pace to Goal &amp; Stretch
            </h3>
          </div>
        </div>
        {latestCheckpoint && (
          <span className="text-[11px] font-bold text-slate-300">
            Based on {cpLabels[latestCheckpoint] || latestCheckpoint.toUpperCase()} reading
          </span>
        )}
      </div>

      {/* Per-checkpoint breakdown — 2026-02 upgraded from tx-only pills
          to immutable Checkpoint Trends rows powered by
          `checkpoint_snapshots` (the frozen-at-save-time rate + captures
          + sales the backend now persists on every PUT /biz/checkpoint).
          The read-time render below prefers `snapshot` if present and
          falls back to the raw tx-only pill for legacy dates so nothing
          disappears on days written before the snapshot feature landed. */}
      {(() => {
        const snaps = tallyData?.checkpoint_snapshots || {};
        const hasSnaps = Object.keys(snaps).length > 0;
        const legacyCps = Object.keys(checkpoints).length > 0;
        if (!hasSnaps && !legacyCps) return null;
        if (hasSnaps) {
          const usd = (n) => {
            if (!Number.isFinite(n) || n === 0) return "$0";
            const abs = Math.abs(n);
            if (abs >= 1000) return `$${(n / 1000).toFixed(1)}K`;
            return `$${Math.round(n).toLocaleString()}`;
          };
          return (
            <div className="mb-4" data-testid="checkpoint-trends">
              <p className="text-[10px] uppercase tracking-[0.22em] font-black text-slate-400 mb-2">
                Checkpoint Trends
              </p>
              <div className="space-y-1.5">
                {["2pm", "5pm", "8pm"].map((cp) => {
                  const s = snaps[cp];
                  if (!s) return null;
                  const label = cpLabels[cp] || cp.toUpperCase();
                  const rate = Math.round((s.rate || 0) * 1000) / 10;
                  const salesDelta = s.sales_delta || 0;
                  const rateDelta = s.rate_delta_pts || 0;
                  const showDelta = (s.sales_delta !== undefined && (Math.abs(salesDelta) >= 1 || Math.abs(rateDelta) >= 0.5));
                  const deltaGood = rateDelta >= 0 && salesDelta >= 0;
                  return (
                    <div
                      key={cp}
                      className={`flex items-center justify-between gap-3 px-3 py-2 rounded-xl ${s.is_latest ? "bg-white/15 ring-1 ring-white/30" : "bg-white/5"}`}
                      data-testid={`checkpoint-trend-${cp}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs font-black text-white shrink-0">{label}</span>
                        <span className="text-sm font-bold text-white tabular-nums">
                          {rate.toFixed(1)}%
                        </span>
                        <span className="text-[11px] text-slate-300 truncate">
                          {usd(s.sales)} · {s.transactions} tx
                        </span>
                      </div>
                      {showDelta && (
                        <span
                          className={`text-[11px] font-black tabular-nums whitespace-nowrap ${deltaGood ? "text-emerald-300" : "text-rose-300"}`}
                          data-testid={`checkpoint-trend-${cp}-delta`}
                        >
                          {salesDelta > 0 ? "+" : ""}{usd(salesDelta)}
                          {rateDelta !== 0 && (
                            <span className="ml-1 opacity-80">({rateDelta > 0 ? "+" : ""}{rateDelta.toFixed(1)}pt)</span>
                          )}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }
        // Legacy fallback — pre-snapshot dates keep the original tx pills.
        return (
          <div className="flex gap-2 mb-4 flex-wrap">
            {["2pm", "5pm", "8pm"].map(cp => {
              if (!checkpoints[cp]) return null;
              const isLatest = cp === latestCheckpoint;
              return (
                <div key={cp} className={`px-3 py-1.5 rounded-full text-xs font-bold ${isLatest ? "bg-white/20 ring-1 ring-white/40 text-white" : "bg-white/5 text-slate-300"}`}>
                  <span className="font-black">{cpLabels[cp]}</span>
                  <span className="ml-1 opacity-75">{checkpoints[cp]} tx</span>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Pace numbers — focused on the actionable NEXT steps (goal +
          stretch). The duplicate Capture Rate big-number column was
          removed 2026-06-13 (already shown on the hero and the
          EmailCaptureGlanceCard — user dedupe ask). */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        <div>
          <p className="text-sm text-slate-400 mb-1">
            To {(goalRate * 100).toFixed(0)}% Goal ({emailsForGoal} emails)
          </p>
          {atGoal ? (
            <div className="flex items-center gap-2">
              <Check weight="bold" className="w-8 h-8 text-emerald-400" />
              <span className="text-xl font-bold text-emerald-400">Hit!</span>
            </div>
          ) : (
            <p className="big-number text-amber-400" data-testid="emails-needed-goal">
              {neededGoal}
            </p>
          )}
          {!atGoal && <p className="text-sm text-slate-400 mt-1">more emails needed</p>}
        </div>

        <div>
          <p className="text-sm text-slate-400 mb-1 flex items-center gap-1">
            <Lightning weight="fill" className="w-3 h-3 text-amber-400" />
            {(stretchRate * 100).toFixed(0)}% Stretch ({emailsForStretch} emails)
          </p>
          {atStretch ? (
            <div className="flex items-center gap-2">
              <Lightning weight="fill" className="w-8 h-8 text-amber-400" />
              <span className="text-xl font-bold text-amber-400">Crushed!</span>
            </div>
          ) : (
            <p className="big-number text-neutral-300" data-testid="emails-needed-stretch">
              {neededStretch}
            </p>
          )}
          {!atStretch && <p className="text-sm text-slate-400 mt-1">more emails needed</p>}
        </div>
      </div>

      {/* Progress bars */}
      <div className="mt-6 space-y-3">
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-400">Goal ({(goalRate*100).toFixed(0)}%)</span>
            <span className={atGoal ? "text-emerald-400" : "text-amber-400"}>
              {(rate * 100).toFixed(1)}%
            </span>
          </div>
          <div className="progress-bar bg-slate-700">
            <div
              className={`progress-bar-fill ${getProgressClass(atGoal, rate)}`}
              style={{ width: `${Math.min(100, (rate / goalRate) * 100)}%` }}
            />
          </div>
        </div>
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-400">Stretch ({(stretchRate*100).toFixed(0)}%)</span>
            <span className={atStretch ? "text-emerald-400" : "text-neutral-400"}>
              {Math.min(100, (rate / stretchRate * 100)).toFixed(0)}%
            </span>
          </div>
          <div className="progress-bar bg-slate-700">
            <div
              className="progress-bar-fill bg-neutral-400"
              style={{ width: `${Math.min(100, (rate / stretchRate) * 100)}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Checkpoint Entry ----
// Exported 2026-02-15 so the Zone Sheet's Eyes-on-the-Biz section can
// host the same per-checkpoint entry experience. Manager Dashboard
// stopped rendering this tile in the same release ("Zone Sheet is the
// only place to enter checkpoints" — user 2026-02-15).
// Helper: MST "now" as seconds since midnight
const getMstSecondsNow = () => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Denver",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).formatToParts(new Date());
  const h = parseInt(parts.find((p) => p.type === "hour").value, 10) % 24;
  const m = parseInt(parts.find((p) => p.type === "minute").value, 10);
  const s = parseInt(parts.find((p) => p.type === "second").value, 10);
  return h * 3600 + m * 60 + s;
};

const parseHHMMToSec = (str, fallback) => {
  if (!str) return fallback;
  const [h, m] = str.split(":").map((n) => parseInt(n, 10));
  if (isNaN(h) || isNaN(m)) return fallback;
  return h * 3600 + m * 60;
};

const formatHM = (str) => {
  if (!str) return "";
  const [h, m] = str.split(":").map((n) => parseInt(n, 10));
  if (isNaN(h) || isNaN(m)) return str;
  const period = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return m === 0 ? `${h12} ${period}` : `${h12}:${String(m).padStart(2, "0")} ${period}`;
};

// Small pulse card shown at the top of every checkpoint tab. Answers
// "what did we do this window and what should we do about it right
// now". Silent (returns null) if there's nothing to show — no
// transactions in the window yet or the slot is a peek at future
// state. Red state (< 25%) surfaces the AI-heuristic coach block plus
// a one-tap huddle blast composer.
const CheckpointPulseCard = ({ pulse, loading }) => {
  if (loading && !pulse) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 mb-4 text-xs text-slate-500 animate-pulse" data-testid="checkpoint-pulse-loading">
        Loading window pulse…
      </div>
    );
  }
  if (!pulse) return null;
  // Nothing worth showing before the slot has any data — skip the card
  // entirely so it doesn't clutter an empty tab.
  if (!pulse.saved && pulse.transactions_in_window === 0 && pulse.captures_in_window === 0) {
    return null;
  }

  const status = pulse.status || "red";
  const rate = pulse.rate_pct ?? 0;
  const barColors = {
    green: { border: "border-l-emerald-500", pill: "bg-emerald-100 text-emerald-800", text: "text-emerald-700" },
    amber: { border: "border-l-amber-500",   pill: "bg-amber-100 text-amber-800",     text: "text-amber-700" },
    red:   { border: "border-l-red-500",     pill: "bg-red-100 text-red-800",         text: "text-red-700" },
  }[status];
  const statusEmoji = status === "green" ? "🔥" : status === "amber" ? "⚠️" : "🚨";
  const statusLabel = status === "green" ? "On fire" : status === "amber" ? "Getting there" : "Below goal";

  return (
    <div
      className={`rounded-xl border border-slate-200 border-l-4 ${barColors.border} bg-white p-3 mb-4`}
      data-testid="checkpoint-pulse"
    >
      {/* Row 1 — the number that matters */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
            Window Pulse · {pulse.window_label}
          </p>
          <div className="mt-0.5 flex items-baseline gap-2">
            <span className={`text-2xl font-bold tabular-nums ${barColors.text}`} data-testid="pulse-rate-pct">
              {rate}%
            </span>
            <span className="text-xs text-slate-500 tabular-nums">
              {pulse.captures_in_window} / {pulse.transactions_in_window}
            </span>
          </div>
        </div>
        <span
          className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${barColors.pill}`}
          data-testid="pulse-status-pill"
        >
          {statusEmoji} {statusLabel}
        </span>
      </div>

      {/* Row 2 — peer + yesterday context strip */}
      {(pulse.peer_rank || pulse.yesterday_same_window_pct != null) && (
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-600">
          {pulse.peer_rank && !pulse.peer_rank.is_top && (
            <span data-testid="pulse-peer-rank">
              <span className="font-semibold text-slate-800">
                #{pulse.peer_rank.rank} of {pulse.peer_rank.total}
              </span>{" "}
              in district today · top: <span className="font-semibold">{pulse.peer_rank.top_code}</span> at {pulse.peer_rank.top_rate_pct}%
            </span>
          )}
          {pulse.peer_rank && pulse.peer_rank.is_top && (
            <span className="text-emerald-700 font-semibold" data-testid="pulse-peer-rank">
              #1 in district today 🏆
            </span>
          )}
          {pulse.yesterday_same_window_pct != null && (
            <span data-testid="pulse-yesterday">
              Yesterday same window: <span className="font-semibold">{pulse.yesterday_same_window_pct}%</span>
            </span>
          )}
        </div>
      )}

      {/* Row 3 (coach card) removed 2026-06-21 per user — team didn't want it. */}

      {/* Row 4 (huddle blast) removed 2026-06-21 per user — team doesn't use it. */}
    </div>
  );
};

export const CheckpointEntry = ({ date, checkpoints, settings, tallyData, onSubmit, onClear, actorId = "", storeId = "", onAfterSave }) => {
  const [activeTab, setActiveTab] = useState(null);
  const [transactions, setTransactions] = useState("");
  const [nowSec, setNowSec] = useState(getMstSecondsNow());

  // ── Pulse (window rate + coach + peer rank + blast draft) ────────────
  // Fetches whenever the active slot, date, or saved checkpoints change
  // so it stays in lockstep with what the manager just entered. Silent
  // on error — the pulse is a nice-to-have layer above the entry form,
  // never a blocker.
  const [pulse, setPulse] = useState(null);
  const [pulseLoading, setPulseLoading] = useState(false);
  useEffect(() => {
    if (!activeTab || activeTab === "preview" || !storeId) { setPulse(null); return; }
    let cancelled = false;
    setPulseLoading(true);
    axios
      .get(`${API}/checkpoint-pulse/${date}`, { params: { slot: activeTab, store_id: storeId } })
      .then((r) => { if (!cancelled) setPulse(r.data || null); })
      .catch(() => { if (!cancelled) setPulse(null); })
      .finally(() => { if (!cancelled) setPulseLoading(false); });
    return () => { cancelled = true; };
  }, [activeTab, date, storeId, checkpoints]);

  // Eyes-on-the-Biz KPI fields (per-checkpoint). Hydrated from /api/biz/{date}
  // when the tab mounts or changes. NPS and Greet inputs were removed
  // 2026-05-13 — team stopped tracking those. Plan $ / Comp Goal $ /
  // ADS / UPT moved out 2026-06-13 to the Daily KPIs editor in
  // EyesOnBizPreview so the printed Zone Sheet captures Today's
  // Targets even on a pre-2pm print run.
  const [sales, setSales] = useState("");
  const [bopis, setBopis] = useState("");
  // actualSales / actualCompSales intentionally no longer user-editable —
  // derived server-side from sum of per-checkpoint Sales $.
  const [biz, setBiz] = useState(null);
  const [saving, setSaving] = useState(false);

  // Preview state for "as of right now" before first checkpoint
  const [previewTx, setPreviewTx] = useState("");
  const [previewSubmitted, setPreviewSubmitted] = useState(null); // { tx, setAt }
  const previewTimerRef = useRef(null);

  // Tick every 15 seconds so tabs unlock when their time arrives
  useEffect(() => {
    const id = setInterval(() => setNowSec(getMstSecondsNow()), 15000);
    return () => clearInterval(id);
  }, []);

  // Always start blank when switching tabs, then hydrate the input values
  // Track whether the user has started editing the current tab's fields. If
  // they have, we must NOT overwrite their in-flight input when the 10-second
  // polling refreshes `checkpoints` / `biz` — that was the "fields erase as
  // you type" bug. Reset to false whenever the user switches tab or date.
  const isDirtyRef = useRef(false);
  const markDirty = () => { isDirtyRef.current = true; };

  // Hydrate checkpoint transactions + KPI fields from whatever has already
  // been saved for that slot so the form reflects the current source of
  // truth (instead of forcing the manager to re-type).
  useEffect(() => {
    isDirtyRef.current = false; // fresh slot — safe to hydrate
    if (!activeTab || activeTab === "preview") { setTransactions(""); return; }
    const saved = (checkpoints || []).find((cp) => cp.checkpoint === activeTab);
    setTransactions(saved?.store_transactions != null ? String(saved.store_transactions) : "");

    let cancelled = false;
    axios.get(`${API}/biz/${date}`)
      .then((r) => {
        if (cancelled) return;
        const data = r.data || {};
        setBiz(data);
        const kpi = (data.checkpoints || {})[activeTab] || {};
        setSales(kpi.sales ?? "");
        setBopis(kpi.bopis ?? "");
      })
      .catch(() => { /* empty skeleton is fine */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, date]);

  // When `checkpoints` refreshes from polling, re-sync ONLY the transaction
  // count and only if the user isn't currently editing — so the display stays
  // in sync with saves from other managers but never clobbers in-flight input.
  useEffect(() => {
    if (isDirtyRef.current) return;
    if (!activeTab || activeTab === "preview") return;
    const saved = (checkpoints || []).find((cp) => cp.checkpoint === activeTab);
    setTransactions(saved?.store_transactions != null ? String(saved.store_transactions) : "");
  }, [checkpoints, activeTab]);

  // Clear preview after 2 min
  useEffect(() => {
    if (previewSubmitted) {
      previewTimerRef.current = setTimeout(() => {
        setPreviewSubmitted(null);
        setPreviewTx("");
      }, 2 * 60 * 1000);
      return () => clearTimeout(previewTimerRef.current);
    }
  }, [previewSubmitted]);

  const todayMst = new Date().toLocaleDateString("en-CA", { timeZone: "America/Denver" });
  const isToday = date === todayMst;

  // Layer per-day overrides (Sunday early-close, etc.) on top of the default
  // checkpoint times. `effectiveFor(date)` returns { cp1_time, cp2_time,
  // cp3_time } in "HH:MM" 24-hour strings — same keys as settings.
  const effectiveFor = (ymd) => {
    const DAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
    const d = new Date(`${ymd}T12:00:00`); // noon avoids any TZ edge case
    const key = DAYS[d.getDay()];
    const ovr = (settings?.day_hours_overrides || {})[key] || {};
    return {
      cp1_time: ovr.cp1_time || settings?.cp1_time || "14:00",
      cp2_time: ovr.cp2_time || settings?.cp2_time || "17:00",
      cp3_time: ovr.cp3_time || settings?.cp3_time || "20:00",
    };
  };
  const eff = effectiveFor(date);

  // Tabs unlock 5 minutes before their scheduled reading time so the manager
  // can prep the form as the minute arrives (then save as soon as the reading
  // is taken). Matches the user's workflow request 2026-04-20.
  const UNLOCK_LEAD_SEC = 5 * 60;
  const cpTimes = {
    "2pm": parseHHMMToSec(eff.cp1_time, 14 * 3600) - UNLOCK_LEAD_SEC,
    "5pm": parseHHMMToSec(eff.cp2_time, 17 * 3600) - UNLOCK_LEAD_SEC,
    "8pm": parseHHMMToSec(eff.cp3_time, 20 * 3600) - UNLOCK_LEAD_SEC,
  };

  // Labels change to match the day's effective hours. On Sundays with the
  // store closing at 7pm, the third tab labels as "7PM" instead of "8PM" etc.
  // Single source of truth lives in /lib/checkpointLabels.js; we pass the
  // active date so a manager paging back to last Sunday's tally sees that
  // Sunday's labels (not today's), respecting per-weekday overrides.
  const tabLabels = getCheckpointLabels(settings, date);

  const isLocked = (cp) => isToday && nowSec < cpTimes[cp];
  const showPreviewTab = isToday && isLocked("2pm");

  // Initial default + auto-switch when time changes
  useEffect(() => {
    if (activeTab === null) {
      setActiveTab(showPreviewTab ? "preview" : "2pm");
      return;
    }
    if (activeTab === "preview" && !showPreviewTab) {
      setActiveTab("2pm");
      return;
    }
    if (activeTab !== "preview" && isLocked(activeTab)) {
      const firstUnlocked = ["2pm", "5pm", "8pm"].find((cp) => !isLocked(cp));
      if (firstUnlocked) setActiveTab(firstUnlocked);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowSec, isToday, showPreviewTab]);

  // Banner deep-link → open the exact missed slot. The missed-reading
  // banner dispatches `wow:openCheckpoint` with the slot id ("2pm",
  // "5pm", "8pm") so tapping "Reading overdue · 2 PM" lands the manager
  // directly in the right tab. (User report 2026-06-12 "the banner is
  // not clickable — should redirect me directly to the entry screen".)
  useEffect(() => {
    const onOpen = (e) => {
      const slot = (e?.detail?.slot || "").toLowerCase();
      if (!slot) return;
      if (["2pm", "5pm", "8pm"].includes(slot) && !isLocked(slot)) {
        setActiveTab(slot);
      }
    };
    window.addEventListener("wow:openCheckpoint", onOpen);
    return () => window.removeEventListener("wow:openCheckpoint", onOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowSec]);

  // Converts a text input into a number, or null for empty / "N/A". Accepts
  // "N/A", "n/a", "NA", or blank as a deliberate "not recorded" for the
  // optional KPI cells.
  const asNum = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const s = String(v).trim().toLowerCase();
    if (s === "n/a" || s === "na" || s === "-") return null;
    const n = Number(s);
    return Number.isNaN(n) ? null : n;
  };

  // Auto-compute # of Orders as sum of BOPIS across all three slots (letting
  // the current tab's entered value win). Read-only in the 8pm summary.
  const autoNumOrders = (() => {
    const cps = { ...(biz?.checkpoints || {}), [activeTab]: { ...((biz?.checkpoints || {})[activeTab] || {}), bopis: asNum(bopis) } };
    const s = Object.values(cps).reduce((acc, c) => acc + (Number(c?.bopis) || 0), 0);
    return s || null;
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLocked(activeTab)) {
      const cpTime = activeTab === "2pm" ? eff.cp1_time : activeTab === "5pm" ? eff.cp2_time : eff.cp3_time;
      toast.error(`${tabLabels[activeTab]} unlocks 5 min before ${formatHM(cpTime)}.`);
      return;
    }
    const tx = parseInt(transactions);
    if (isNaN(tx) || tx < 0) {
      toast.error("Transactions must be 0 or a whole number.");
      return;
    }
    setSaving(true);
    try {
      // 1. Save the transaction count first (also snapshots READS server-side).
      await onSubmit({ date, checkpoint: activeTab, store_transactions: tx });
      // 2. Save the Eyes-on-the-Biz KPI cells for this slot.
      await axios.put(`${API}/biz/checkpoint`, {
        date,
        checkpoint: activeTab,
        sales: asNum(sales),
        bopis: asNum(bopis),
        actor_id: actorId,
      });
      // 3. On 2pm, also save day-level # of Orders (derived from BOPIS sum).
      //    Plan $ / Comp Goal $ used to live here but moved to the
      //    Daily KPIs editor in EyesOnBizPreview on 2026-06-13 so a
      //    morning print of the Zone Sheet captures Today's Targets
      //    without waiting for the 2pm clock unlock.
      //    Actual $ / Actual Comp $ are derived server-side from the
      //    sum of per-checkpoint Sales $.
      if (activeTab === "2pm") {
        await axios.put(`${API}/biz/day`, {
          date,
          num_orders: autoNumOrders,
          actor_id: actorId,
        });
      }
      // 4. Refetch the local `biz` doc so the locked KPI card paints
      //    the just-saved Sales / Reads / BOPIS values immediately.
      //    (User report 2026-06-15: after save the "transactions: 53"
      //    row appeared but Sales/Reads/BOPIS stayed blank/dash until
      //    a full Zone Sheet exit+reopen. Root cause: `biz` is only
      //    fetched in the [activeTab, date] hydration effect and
      //    parent `bizRefreshKey` doesn't trigger it. Direct refetch
      //    here keeps the source of truth in sync without remount.)
      try {
        const r = await axios.get(`${API}/biz/${date}`);
        setBiz(r.data || null);
      } catch { /* non-critical — parent poll will catch up within 10s */ }
      onAfterSave && onAfterSave();
      isDirtyRef.current = false; // allow polling to re-sync with server again
    } catch (err) {
      toast.error("Couldn't save KPIs — transaction count was saved");
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewSubmit = (e) => {
    e.preventDefault();
    const tx = parseInt(previewTx);
    if (isNaN(tx) || tx < 0) {
      toast.error("Transactions must be 0 or a whole number.");
      return;
    }
    setPreviewSubmitted({ tx, setAt: Date.now() });
  };

  const savedData = activeTab && activeTab !== "preview" ? checkpoints.find((cp) => cp.checkpoint === activeTab) : null;
  const totalEmails = tallyData?.total_emails || 0;
  const goalRate = settings?.goal_rate || 0.35;
  const previewRate = previewSubmitted?.tx > 0 ? totalEmails / previewSubmitted.tx : 0;
  const previewAtGoal = previewRate >= goalRate;

  if (!activeTab) {
    return (
      <div className="dashboard-card" data-testid="checkpoint-entry">
        <div className="flex items-center gap-2 mb-4">
          <Clock weight="bold" className="w-5 h-5 text-slate-600" />
          <h3 className="text-lg font-bold" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>Transaction Checkpoints</h3>
        </div>
        <p className="text-sm text-slate-400" data-testid="no-active-checkpoint">No checkpoint available right now — unlocks shortly before each reading.</p>
      </div>
    );
  }

  const dayClosureReason = ((settings?.closed_dates || {})[date] || "").trim();

  return (
    <div className="dashboard-card" data-testid="checkpoint-entry">
      <div className="flex items-center gap-2 mb-4">
        <Clock weight="bold" className="w-5 h-5 text-slate-600" />
        <h3 className="text-lg font-bold" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
          Transaction Checkpoints
        </h3>
      </div>

      {dayClosureReason && (
        <div
          className="mb-4 rounded-lg border-2 border-rose-300 bg-rose-50 px-4 py-3 flex items-start gap-3"
          data-testid="checkpoint-closed-banner"
        >
          <Lock weight="bold" className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-black uppercase tracking-wider text-rose-900" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
              Store closed · {dayClosureReason}
            </p>
            <p className="text-xs text-rose-800 mt-0.5">
              Checkpoints are locked for this date. Remove the closure from <strong>Settings → Store Hours → Closures</strong> to re-enable.
            </p>
          </div>
        </div>
      )}

      <div className={`flex gap-2 mb-4 border-b border-slate-200 overflow-x-auto ${dayClosureReason ? "opacity-50 pointer-events-none" : ""}`}>
        {showPreviewTab && (
          <button
            className={`checkpoint-tab ${activeTab === "preview" ? "active" : ""}`}
            onClick={() => setActiveTab("preview")}
            data-testid="checkpoint-preview-tab"
            title="Quick preview of current capture rate — not saved"
          >
            Right Now
          </button>
        )}
        {["2pm", "5pm", "8pm"].map((cp) => {
          const saved = checkpoints.some((c) => c.checkpoint === cp);
          const locked = isLocked(cp);
          const cpTime = cp === "2pm" ? eff.cp1_time : cp === "5pm" ? eff.cp2_time : eff.cp3_time;
          return (
            <button
              key={cp}
              className={`checkpoint-tab ${activeTab === cp ? "active" : ""} ${locked ? "opacity-40 cursor-not-allowed" : ""}`}
              onClick={() => !locked && setActiveTab(cp)}
              disabled={locked}
              data-testid={`checkpoint-${cp}-tab`}
              title={locked ? `Unlocks 5 min before ${formatHM(cpTime)}` : ""}
            >
              {tabLabels[cp]}
              {locked && <Lock weight="bold" className="w-3 h-3 ml-1 inline text-slate-400" />}
              {saved && !locked && (
                <Check weight="bold" className="w-3 h-3 ml-1 inline text-emerald-500" />
              )}
            </button>
          );
        })}
      </div>

      {/* Preview tab content */}
      {activeTab === "preview" && (
        <div className="rounded-md border border-dashed border-blue-300 bg-blue-50 p-4" data-testid="preview-panel">
          <p className="text-xs uppercase tracking-wider font-semibold mb-3" style={{ color: "var(--fb-blue)" }}>As of Right Now</p>
          {!previewSubmitted ? (
            <form onSubmit={handlePreviewSubmit} className="flex flex-col sm:flex-row gap-2">
              <Input
                type="number"
                value={previewTx}
                onChange={(e) => setPreviewTx(e.target.value)}
                placeholder="Current POS transactions"
                className="flex-1"
                data-testid="preview-tx-input"
              />
              <Button type="submit" className="fb-btn-primary" data-testid="preview-submit">
                Preview %
              </Button>
            </form>
          ) : (
            <div className="flex items-end justify-between gap-3 flex-wrap">
              <div>
                <p className="text-3xl font-black" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", color: previewAtGoal ? "#059669" : "#D97706" }} data-testid="preview-rate">
                  {(previewRate * 100).toFixed(1)}%
                </p>
                <p className="text-xs text-slate-500">{totalEmails} emails / {previewSubmitted.tx} transactions</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[11px] text-slate-400 italic">Auto-clears in 2 min. Not saved.</span>
                <button
                  onClick={() => { clearTimeout(previewTimerRef.current); setPreviewSubmitted(null); setPreviewTx(""); }}
                  className="text-xs font-medium text-slate-500 hover:text-slate-700"
                  data-testid="preview-clear"
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Checkpoint form content */}
      {activeTab !== "preview" && (
        <>
          <CheckpointPulseCard
            pulse={pulse}
            loading={pulseLoading}
          />
          {/* Show saved data if exists */}
          {savedData && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 mb-4 flex items-center justify-between" data-testid="checkpoint-saved-info">
              <p className="text-sm text-emerald-700">
                <Check weight="bold" className="w-4 h-4 inline mr-1" />
                <span className="font-bold">{savedData.updated_by_name || "Manager"}</span> saved {tabLabels[activeTab] || activeTab.toUpperCase()}: <span className="font-bold">{savedData.store_transactions}</span> transactions
                {savedData.updated_at && (
                  <span className="text-emerald-500 ml-1">
                    at {new Date(savedData.updated_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                  </span>
                )}
              </p>
              <button
                onClick={() => onClear(date, activeTab)}
                className="text-xs text-red-500 hover:text-red-700 font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors"
                data-testid="clear-checkpoint-button"
              >
                <X weight="bold" className="w-3 h-3 inline mr-0.5" />
                Clear
              </button>
            </div>
          )}

          {isLocked(activeTab) ? (
            <div className="rounded-md bg-slate-50 border border-slate-200 p-4 text-center" data-testid="checkpoint-locked-notice">
              <Lock weight="bold" className="w-5 h-5 text-slate-400 mx-auto mb-2" />
              <p className="text-sm font-semibold text-slate-700">{tabLabels[activeTab]} checkpoint is locked</p>
              <p className="text-xs text-slate-500 mt-1">
                Unlocks 5 min before {formatHM(
                  activeTab === "2pm" ? eff.cp1_time :
                  activeTab === "5pm" ? eff.cp2_time :
                                        eff.cp3_time
                )}.
              </p>
            </div>
          ) : savedData ? (
            /* Locked, read-only view of the saved KPI cells. After save
               the inputs collapse into display chips so a manager can't
               accidentally type over them (user bug 2026-06-13 "saved
               checkpoint fields are still editable"). The Clear button
               above is the ONLY path back to editing — pressing it
               wipes the saved doc, which re-renders this branch in the
               <form> mode below. Save/Update button is hidden entirely
               while locked, per user spec "Save/Update button should be
               hidden or disabled". */
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3" data-testid="checkpoint-saved-locked">
              <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">{tabLabels[activeTab]} KPIs · locked</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div>
                  <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Transactions</label>
                  <div
                    className="flex h-10 w-full items-center rounded-md border border-slate-200 bg-white px-3 text-sm font-bold text-slate-900 tabular-nums"
                    data-testid={`checkpoint-${activeTab}-transactions-locked`}
                  >
                    {savedData.store_transactions ?? <span className="text-slate-400 font-normal">—</span>}
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Sales $</label>
                  <div
                    className="flex h-10 w-full items-center rounded-md border border-slate-200 bg-white px-3 text-sm font-bold text-slate-900 tabular-nums"
                    data-testid={`biz-sales-${activeTab}-locked`}
                  >
                    {(() => {
                      const v = (biz?.checkpoints || {})[activeTab]?.sales;
                      return v == null || v === "" ? <span className="text-slate-400 font-normal">—</span> : `$${Number(v).toLocaleString()}`;
                    })()}
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Reads</label>
                  <div
                    className="flex h-10 w-full items-center rounded-md border border-slate-200 bg-white px-3 text-sm font-bold text-slate-900 tabular-nums"
                    data-testid={`biz-reads-${activeTab}-locked`}
                  >
                    {(() => {
                      const v = (biz?.checkpoints || {})[activeTab]?.reads;
                      return v == null || v === "" ? <span className="text-slate-400 font-normal">—</span> : v;
                    })()}
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">BOPIS</label>
                  <div
                    className="flex h-10 w-full items-center rounded-md border border-slate-200 bg-white px-3 text-sm font-bold text-slate-900 tabular-nums"
                    data-testid={`biz-bopis-${activeTab}-locked`}
                  >
                    {(() => {
                      const v = (biz?.checkpoints || {})[activeTab]?.bopis;
                      return v == null || v === "" ? <span className="text-slate-400 font-normal">—</span> : v;
                    })()}
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-slate-400 mt-1.5 leading-snug">
                Locked once saved. Tap <span className="font-semibold">Clear</span> above to unlock and re-enter.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              {/* KPI entry card — Transactions, Sales, READS (auto), BOPIS
                  in one consistent 5-up grid so the form doesn't feel "bolted
                  on" with a separate transactions input above. READS is
                  read-only — it's the cumulative email-capture count
                  snapshotted by the backend the moment the slot is saved. */}
              <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
                <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">{tabLabels[activeTab]} KPIs</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Transactions</label>
                    <Input
                      type="number"
                      value={transactions}
                      onChange={(e) => { markDirty(); setTransactions(e.target.value); }}
                      placeholder={savedData ? String(savedData.store_transactions) : "0"}
                      data-testid={`checkpoint-${activeTab}-transactions`}
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Sales $</label>
                    <Input type="number" step="0.01" value={sales} onChange={(e) => { markDirty(); setSales(e.target.value); }} placeholder="0" data-testid={`biz-sales-${activeTab}`} />
                  </div>
                  {/* READS (read-only). Sourced from the biz_metrics doc the
                      backend updates on every checkpoint save, NOT from the
                      live tally — so it stays pinned to the snapshot taken
                      at the moment the slot was last saved. Shows "—" when
                      that slot hasn't been saved yet (no snapshot exists). */}
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Reads</label>
                    <div
                      className="flex h-10 w-full items-center rounded-md border border-slate-200 bg-white px-3 text-sm font-bold text-slate-900 tabular-nums"
                      data-testid={`biz-reads-${activeTab}`}
                    >
                      {(() => {
                        const v = (biz?.checkpoints || {})[activeTab]?.reads;
                        return v == null || v === "" ? <span className="text-slate-400 font-normal">—</span> : v;
                      })()}
                    </div>
                  </div>
                  {/* NPS % and Greet % inputs removed 2026-05-13 — team
                      stopped tracking those metrics. Sales + BOPIS are
                      the only KPI cells now. */}
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">BOPIS</label>
                    <Input type="number" value={bopis} onChange={(e) => { markDirty(); setBopis(e.target.value); }} placeholder="0" data-testid={`biz-bopis-${activeTab}`} />
                  </div>
                </div>
                <p className="text-[10px] text-slate-400 mt-1.5 leading-snug">
                  Reads auto-snapshot when you save the time slot — total emails captured up to that moment.
                </p>
              </div>

              {/* Today's targets (Plan $ / Comp Goal $) moved to the Daily
                  KPIs editor in EyesOnBizPreview on 2026-06-13 so a
                  morning print of the Zone Sheet captures them even
                  before the 2pm clock unlock. The 2pm checkpoint
                  panel now only collects Sales $ + BOPIS for this
                  slot. */}

              <Button
                type="submit"
                className="w-full fb-btn-primary"
                disabled={saving}
                data-testid="submit-checkpoint-button"
              >
                {saving ? "Saving…" : (savedData ? `Update ${tabLabels[activeTab] || activeTab.toUpperCase()}` : `Save ${tabLabels[activeTab] || activeTab.toUpperCase()}`)}
              </Button>
            </form>
          )}
        </>
      )}
    </div>
  );
};

// ---- Crew Management ----
export const CrewManagement = ({ crew, isAdmin = false, onAdd, onUpdate, onDeactivate, onReactivate, onDeleteForever, actorId = "" }) => {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmployeeId, setNewEmployeeId] = useState("");
  const [newPosition, setNewPosition] = useState("");
  const [newIsManager, setNewIsManager] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");

  // Per-member feature suspension for policy violations (user ask 2026-10-11).
  const SUSP_FEATURES = [
    { key: "messaging", label: "Messaging & DMs" },
    { key: "stories", label: "Posting Stories" },
    { key: "recognition", label: "High Fives" },
    { key: "voice", label: "Walkie / voice" },
  ];
  const [suspendFor, setSuspendFor] = useState(null);
  const [suspFeatures, setSuspFeatures] = useState({});
  const [suspMode, setSuspMode] = useState("forever"); // "days" | "forever"
  const [suspDays, setSuspDays] = useState("7");
  const [suspReason, setSuspReason] = useState("");
  const [suspBusy, setSuspBusy] = useState(false);

  const suspEffective = (m) => {
    if (!m) return false;
    if (m.suspended_until) {
      try { if (new Date(m.suspended_until) < new Date()) return false; } catch { /* ignore */ }
    }
    return SUSP_FEATURES.some((f) => m[`suspend_${f.key}`]);
  };
  const suspLabels = (m) => SUSP_FEATURES.filter((f) => m[`suspend_${f.key}`]).map((f) => f.label);

  const openSuspend = (member) => {
    const init = {};
    SUSP_FEATURES.forEach((f) => { init[f.key] = !!member[`suspend_${f.key}`]; });
    if (!suspEffective(member)) init.messaging = true; // sensible default
    setSuspFeatures(init);
    if (member.suspended_until && suspEffective(member)) {
      setSuspMode("days");
      try {
        const d = Math.max(1, Math.ceil((new Date(member.suspended_until) - new Date()) / 86400000));
        setSuspDays(String(d));
      } catch { setSuspDays("7"); }
    } else { setSuspMode("forever"); setSuspDays("7"); }
    setSuspReason(member.suspend_reason || "");
    setSuspendFor(member);
  };

  const applySuspend = async () => {
    if (!suspendFor || suspBusy) return;
    const features = SUSP_FEATURES.filter((f) => suspFeatures[f.key]).map((f) => f.key);
    if (!features.length) { toast.error("Pick at least one thing to pause."); return; }
    setSuspBusy(true);
    try {
      await axios.put(`${API}/crew/${suspendFor.id}/suspend`, {
        actor_id: actorId,
        features,
        days: suspMode === "days" ? Number(suspDays || 0) : null,
        reason: suspReason.trim(),
      });
      toast.success(`${suspendFor.first_name}'s access updated.`);
      setSuspendFor(null);
      window.dispatchEvent(new CustomEvent("wow:crew-roster-refresh"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update");
    } finally { setSuspBusy(false); }
  };

  const restoreSuspend = async (member) => {
    if (suspBusy) return;
    setSuspBusy(true);
    try {
      await axios.put(`${API}/crew/${member.id}/restore`, { actor_id: actorId });
      toast.success(`${member.first_name}'s access restored.`);
      setSuspendFor(null);
      window.dispatchEvent(new CustomEvent("wow:crew-roster-refresh"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't restore");
    } finally { setSuspBusy(false); }
  };

  // Canonical org-chart positions (mirrors backend POSITIONS). Managers/admins
  // assign each crew member to one of these so the Team tile tree is populated.
  const POSITIONS = [
    "Store Manager",
    "Merchandise Manager",
    "Customer Experience Manager",
    "Full Time Support Lead",
    "Part Time Support Lead",
    "Wow Crew",
    "Seasonal Wow Crew",
  ];

  // Sort alphabetically by first_name (case-insensitive) so the roster is
  // always in a predictable order instead of raw API/insertion order.
  const byName = (a, b) =>
    (a.first_name || "").localeCompare(b.first_name || "", undefined, { sensitivity: "base" });
  const activeCrew = crew.filter((c) => c.active).sort(byName);
  const inactiveCrew = crew.filter((c) => !c.active).sort(byName);

  // Detect "import columns swapped" candidates: first_name is all digits AND
  // employee_id is not all digits. Matches the user-report case where a CSV
  // import landed names in the ID column and vice-versa. We only show the
  // fix button when the pattern exists, so the UI stays clean otherwise.
  // (Added 2026-05-22 — user couldn't re-edit because the existing edit form
  // doesn't let you change employee_id, only first_name + email.)
  const swapCandidates = activeCrew.filter(
    (c) => /^\d+$/.test(c.first_name || "") && !/^\d+$/.test(c.employee_id || "")
  );
  const [swapBusy, setSwapBusy] = useState(false);

  const handleFixSwap = async () => {
    if (!swapCandidates.length || swapBusy) return;
    // Two-step confirm to avoid accidental clicks — show the count and a
    // sample of what will be swapped before committing.
    const sample = swapCandidates.slice(0, 3)
      .map((c) => `  ${c.first_name} ↔ ${c.employee_id}`).join("\n");
    const more = swapCandidates.length > 3 ? `\n  …and ${swapCandidates.length - 3} more` : "";
    const ok = window.confirm(
      `Fix ${swapCandidates.length} row(s) where the name and ID look swapped?\n\n${sample}${more}\n\nThis only swaps first_name ↔ employee_id; nothing else changes.`
    );
    if (!ok) return;
    setSwapBusy(true);
    try {
      const params = new URLSearchParams({ actor_id: actorId || "", apply: "true" }).toString();
      const res = await axios.post(`${API}/admin/fix-swapped-name-id?${params}`);
      // toast import is via the outer SettingsPage; use window event so we
      // don't have to thread a prop here.
      window.dispatchEvent(new CustomEvent("wow:toast-success",
        { detail: `Swapped name ↔ ID on ${res.data.swapped || 0} crew row(s).` }));
      // Force a roster refetch so the new ordering appears immediately.
      window.dispatchEvent(new CustomEvent("wow:crew-roster-refresh"));
    } catch (e) {
      console.error("[CrewManagement] fix-swap failed", e);
      window.dispatchEvent(new CustomEvent("wow:toast-error",
        { detail: e?.response?.data?.detail || "Couldn't apply the fix. Try again." }));
    } finally {
      setSwapBusy(false);
    }
  };

  const handleAdd = () => {
    if (!newName.trim() || !newEmployeeId.trim()) return;
    onAdd({ first_name: newName.trim(), employee_id: newEmployeeId.trim(), position: newPosition, is_manager: newIsManager });
    setNewName(""); setNewEmployeeId(""); setNewPosition(""); setNewIsManager(false); setIsAddOpen(false);
  };

  const handleUpdate = (id) => {
    const updateData = {};
    if (editName.trim()) updateData.first_name = editName.trim();
    if (Object.keys(updateData).length) onUpdate(id, updateData);
    // Save notification_email separately (hits /crew/{id}/profile). Allow
    // clearing with an empty string so managers can remove a wrong address.
    const trimmedEmail = (editEmail || "").trim();
    const member = crew.find((c) => c.id === id);
    const currentEmail = (member?.notification_email || "").trim();
    if (trimmedEmail !== currentEmail) {
      onUpdate(id, { notification_email: trimmedEmail }, { isProfile: true });
    }
    setEditingId(null); setEditName(""); setEditEmail("");
  };

  return (
    <div className="dashboard-card" data-testid="crew-management">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users weight="bold" className="w-5 h-5 text-slate-600" />
          <h3 className="text-lg font-bold" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
            Wow Crew ({activeCrew.length})
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {/* Fix swapped name/ID rows (one-click) — only shown when the
              pattern is detected (first_name all digits + employee_id not
              all digits). User report 2026-05-22: imported with the
              columns reversed and couldn't edit employee_id from the
              normal edit form. */}
          {swapCandidates.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleFixSwap}
              disabled={swapBusy}
              className="border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
              title={`Detected ${swapCandidates.length} row(s) where first_name looks like an employee ID.`}
              data-testid="fix-swapped-name-id-button"
            >
              <ArrowsLeftRight weight="bold" className="w-4 h-4 mr-1" />
              {swapBusy ? "Fixing…" : `Fix ${swapCandidates.length} swap${swapCandidates.length === 1 ? "" : "s"}`}
            </Button>
          )}
          <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="fb-btn-primary" data-testid="add-crew-button">
              <Plus weight="bold" className="w-4 h-4 mr-1" /> Add
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="add-crew-dialog">
            <DialogHeader><DialogTitle>Add Crew Member</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-3">
              <div>
                <label className="block text-sm font-medium mb-1">First Name</label>
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Name" data-testid="new-crew-name-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Employee ID</label>
                <Input value={newEmployeeId} onChange={(e) => setNewEmployeeId(e.target.value)} placeholder="Employee ID" data-testid="new-crew-id-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Position</label>
                <select
                  value={newPosition}
                  onChange={(e) => setNewPosition(e.target.value)}
                  className="w-full text-sm px-3 py-2 rounded-md border border-slate-200 bg-white hover:border-slate-300 focus:border-blue-400 focus:outline-none"
                  data-testid="new-crew-position-select"
                >
                  <option value="">Unassigned</option>
                  {POSITIONS.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="is-manager" checked={newIsManager} onChange={(e) => setNewIsManager(e.target.checked)} className="w-4 h-4" />
                <label htmlFor="is-manager" className="text-sm font-medium">Manager access</label>
              </div>
              <Button onClick={handleAdd} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="confirm-add-crew-button">
                Add Crew Member
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <ScrollArea className="h-96">
        <div className="space-y-1 pr-3">
          {activeCrew.map((member) => (
            <div key={member.id} className="flex items-center justify-between p-2 rounded-md hover:bg-slate-50" data-testid={`crew-member-${member.id}`}>
              {editingId === member.id ? (
                <div className="flex flex-col gap-2 flex-1">
                  <div className="flex items-center gap-2">
                    <Input value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Name" className="flex-1" autoFocus />
                    <Button size="sm" onClick={() => handleUpdate(member.id)} data-testid={`save-crew-${member.id}`}><Check weight="bold" className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} className="text-slate-600 hover:text-slate-900"><X weight="bold" className="w-4 h-4" /></Button>
                  </div>
                  {/* Position selector — only shown while the pencil-edit mode is
                      active. Writes through onUpdate with isPosition so the server
                      routes to /api/crew/{id}/position (audit + team ping). */}
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Position</span>
                    <select
                      value={member.position || ""}
                      onChange={(e) => onUpdate(member.id, { position: e.target.value }, { isPosition: true })}
                      className="flex-1 text-xs px-2 py-1.5 rounded-md border border-slate-200 bg-white hover:border-slate-300 focus:border-blue-400 focus:outline-none"
                      data-testid={`position-select-${member.id}`}
                    >
                      <option value="">Unassigned</option>
                      {POSITIONS.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                  {/* Notification email — required for blasts, digests, DMs to reach
                      this crew member. Saved on tap of the check button above. */}
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 whitespace-nowrap">Email</span>
                    <Input
                      type="email"
                      value={editEmail}
                      onChange={(e) => setEditEmail(e.target.value)}
                      placeholder="crewmember@example.com"
                      className="flex-1 text-xs h-8"
                      data-testid={`edit-email-${member.id}`}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <p className="font-medium">
                      {member.first_name}
                      <button
                        className={`ml-2 text-xs px-2 py-0.5 rounded-full font-semibold transition-colors ${
                          member.is_manager
                            ? "bg-red-100 text-red-700 hover:bg-neutral-100 hover:text-neutral-500"
                            : "bg-neutral-100 text-neutral-400 hover:bg-red-100 hover:text-red-700"
                        }`}
                        onClick={() => onUpdate(member.id, { is_manager: !member.is_manager })}
                        title={member.is_manager ? "Click to remove manager access" : "Click to grant manager access"}
                        data-testid={`toggle-mgr-${member.id}`}
                      >
                        {member.is_manager ? "MGR" : "CREW"}
                      </button>
                      {member.is_manager && isAdmin && (
                        <button
                          className={`ml-1 text-xs px-2 py-0.5 rounded-full font-semibold transition-colors ${
                            member.is_admin
                              ? "bg-amber-100 text-amber-800 hover:bg-amber-50"
                              : "bg-slate-100 text-slate-400 hover:bg-amber-100 hover:text-amber-700"
                          }`}
                          onClick={() => onUpdate(member.id, { is_admin: !member.is_admin })}
                          title={member.is_admin ? "Click to revoke admin access" : "Click to grant admin access"}
                          data-testid={`toggle-admin-${member.id}`}
                        >
                          {member.is_admin ? "ADMIN" : "+ ADMIN"}
                        </button>
                      )}
                      {member.is_admin && !isAdmin && (
                        <span className="ml-1 text-xs px-2 py-0.5 rounded-full font-semibold bg-amber-100 text-amber-800" data-testid={`admin-badge-${member.id}`}>
                          ADMIN
                        </span>
                      )}
                      {/* Training Store Manager toggle — admin-only. Grants
                          read-only district shadow access (no mirror). Lets
                          the org flag any SM as "training to become a DM"
                          without giving them DM write powers. (User ask
                          2026-05-25 for Caitlyn at 3015, scalable to anyone
                          else who enters DM training.) */}
                      {member.is_manager && isAdmin && (
                        <button
                          className={`ml-1 text-xs px-2 py-0.5 rounded-full font-semibold transition-colors ${
                            member.is_training_sm
                              ? "bg-violet-100 text-violet-800 hover:bg-violet-50"
                              : "bg-slate-100 text-slate-400 hover:bg-violet-100 hover:text-violet-700"
                          }`}
                          onClick={() => onUpdate(member.id, { is_training_sm: !member.is_training_sm })}
                          title={member.is_training_sm ? "Click to remove Training SM access (district shadow)" : "Click to grant Training SM access (read-only district shadow)"}
                          data-testid={`toggle-tsm-${member.id}`}
                        >
                          {member.is_training_sm ? "TSM" : "+ TSM"}
                        </button>
                      )}
                      {member.is_training_sm && !isAdmin && (
                        <span className="ml-1 text-xs px-2 py-0.5 rounded-full font-semibold bg-violet-100 text-violet-800" data-testid={`tsm-badge-${member.id}`}>
                          TSM
                        </span>
                      )}
                      {suspEffective(member) && (
                        <span
                          className="ml-1 text-xs px-2 py-0.5 rounded-full font-semibold bg-red-600 text-white inline-flex items-center gap-1"
                          title={`Paused: ${suspLabels(member).join(", ")}${member.suspended_until ? ` · until ${new Date(member.suspended_until).toLocaleDateString()}` : " · until restored"}${member.suspend_reason ? ` · ${member.suspend_reason}` : ""}`}
                          data-testid={`suspended-badge-${member.id}`}
                        >
                          <Prohibit weight="bold" className="w-3 h-3" /> PAUSED
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-slate-500">
                      ID: {member.employee_id}
                      {member.position && (
                        <span className="ml-2 text-slate-600">· {member.position}</span>
                      )}
                      {!member.notification_email && (
                        <span className="ml-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-bold bg-amber-50 text-amber-700 border border-amber-200" title="No notification email on file — this crew member won't receive blast / digest / DM emails" data-testid={`no-email-badge-${member.id}`}>
                          no email
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button className="p-1 rounded hover:bg-slate-200 transition-colors" onClick={() => { setEditingId(member.id); setEditName(member.first_name); setEditEmail(member.notification_email || ""); }} data-testid={`edit-crew-${member.id}`} title="Edit name / email">
                      <PencilSimple className="w-4 h-4 text-slate-500" />
                    </button>
                    <button
                      className="p-1.5 rounded hover:bg-amber-100 transition-colors"
                      onClick={async () => {
                        // Reset this crew's 4-digit PIN. Forces them through
                        // first-time setup on their next login. Manager-only
                        // (server enforces; client just hides for non-mgrs).
                        if (!window.confirm(`Reset ${member.first_name}'s PIN? They'll be asked to set a new one on next login.`)) return;
                        try {
                          await axios.post(`${API}/auth/reset-pin/${member.id}`, { actor_id: actorId });
                          toast.success(`${member.first_name}'s PIN has been reset.`);
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Couldn't reset PIN");
                        }
                      }}
                      data-testid={`reset-pin-${member.id}`}
                      title="Reset PIN — forces them to set a new one on next login"
                    >
                      <Key className="w-4 h-4 text-amber-600" />
                    </button>
                    <button
                      className="p-1.5 rounded hover:bg-blue-100 transition-colors"
                      onClick={() => {
                        // Open the transfer request sheet. Managers can request
                        // transfers for their own store's crew; DM/admin can too.
                        // The actual move requires admin/DM approval — this just
                        // fires the request. (2026-02-27 user ask "make
                        // transfers easier than deleting them off the roster".)
                        window.dispatchEvent(new CustomEvent("wow:open-transfer-request", { detail: { crew: member } }));
                      }}
                      data-testid={`transfer-crew-${member.id}`}
                      title="Request transfer to another store"
                      aria-label="Request transfer"
                    >
                      <ArrowsLeftRight className="w-4 h-4 text-blue-600" />
                    </button>
                    <button
                      className={`p-1.5 rounded transition-colors ${suspEffective(member) ? "bg-red-100 hover:bg-red-200" : "hover:bg-red-50"}`}
                      onClick={() => openSuspend(member)}
                      data-testid={`suspend-crew-${member.id}`}
                      title={suspEffective(member) ? "Manage / lift paused features" : "Pause features (policy violation)"}
                      aria-label="Pause features"
                    >
                      <Prohibit className={`w-4 h-4 ${suspEffective(member) ? "text-red-600" : "text-slate-400"}`} />
                    </button>
                    <button className="p-1.5 rounded hover:bg-red-100 transition-colors" onClick={() => onDeactivate(member.id)} data-testid={`deactivate-crew-${member.id}`} title="Deactivate (keeps history)">
                      <UserMinus className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}

          {inactiveCrew.length > 0 && (
            <div className="mt-4 pt-3 border-t border-slate-200">
              <p className="text-xs text-slate-500 mb-2 uppercase tracking-wider font-semibold">
                Inactive ({inactiveCrew.length})
              </p>
              {inactiveCrew.map((member) => (
                <div key={member.id} className="flex items-center justify-between p-2 opacity-60">
                  <div>
                    <p className="text-sm">{member.first_name}</p>
                    <p className="text-xs text-slate-400">ID: {member.employee_id}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => onReactivate(member.id)} className="text-green-700 hover:text-green-800 hover:bg-green-50" data-testid={`reactivate-crew-${member.id}`}>
                      <UserPlus className="w-4 h-4 mr-1" /> Reactivate
                    </Button>
                    {isAdmin && onDeleteForever && (
                      <button
                        className="p-1.5 rounded hover:bg-red-100 transition-colors"
                        onClick={() => onDeleteForever(member.id)}
                        data-testid={`delete-crew-${member.id}`}
                        title="Delete forever — wipes all their data"
                        aria-label="Delete forever"
                      >
                        <Trash className="w-4 h-4 text-red-600" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </ScrollArea>

      <Dialog open={!!suspendFor} onOpenChange={(o) => { if (!o) setSuspendFor(null); }}>
        <DialogContent data-testid="suspend-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Prohibit weight="bold" className="w-5 h-5 text-red-600" />
              Pause access{suspendFor ? ` — ${suspendFor.first_name}` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">What to pause</p>
              <div className="grid grid-cols-2 gap-2">
                {SUSP_FEATURES.map((f) => {
                  const on = !!suspFeatures[f.key];
                  return (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => setSuspFeatures((s) => ({ ...s, [f.key]: !s[f.key] }))}
                      className={`text-sm font-medium px-3 py-2 rounded-lg border text-left transition-colors ${
                        on ? "bg-red-50 border-red-300 text-red-700" : "bg-white border-slate-200 text-slate-600 hover:border-red-200"
                      }`}
                      data-testid={`suspend-feature-${f.key}`}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        {on ? <Prohibit weight="bold" className="w-3.5 h-3.5" /> : <Check weight="bold" className="w-3.5 h-3.5 opacity-0" />}
                        {f.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">For how long</p>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSuspMode("forever")}
                  className={`text-sm font-medium px-3 py-1.5 rounded-full border transition-colors ${suspMode === "forever" ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200"}`}
                  data-testid="suspend-mode-forever"
                >
                  Until I restore it
                </button>
                <button
                  type="button"
                  onClick={() => setSuspMode("days")}
                  className={`text-sm font-medium px-3 py-1.5 rounded-full border transition-colors ${suspMode === "days" ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200"}`}
                  data-testid="suspend-mode-days"
                >
                  Set days
                </button>
                {suspMode === "days" && (
                  <div className="flex items-center gap-1.5">
                    <Input
                      type="number"
                      min="1"
                      max="365"
                      value={suspDays}
                      onChange={(e) => setSuspDays(e.target.value)}
                      className="w-20"
                      data-testid="suspend-days-input"
                    />
                    <span className="text-sm text-slate-500">days</span>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Reason (optional)</label>
              <Input
                value={suspReason}
                onChange={(e) => setSuspReason(e.target.value)}
                placeholder="e.g. Inappropriate language in a blast"
                maxLength={280}
                data-testid="suspend-reason-input"
              />
            </div>

            <div className="flex items-center justify-between gap-2 pt-1">
              {suspendFor && suspEffective(suspendFor) ? (
                <button
                  type="button"
                  onClick={() => restoreSuspend(suspendFor)}
                  disabled={suspBusy}
                  className="inline-flex items-center gap-1.5 text-sm font-bold px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
                  data-testid="suspend-restore-all"
                >
                  <ShieldCheck weight="bold" className="w-4 h-4" /> Restore all
                </button>
              ) : <span />}
              <div className="flex items-center gap-2">
                <Button variant="ghost" onClick={() => setSuspendFor(null)} data-testid="suspend-cancel">Cancel</Button>
                <button
                  type="button"
                  onClick={applySuspend}
                  disabled={suspBusy}
                  className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
                  data-testid="suspend-apply"
                >
                  <Prohibit weight="bold" className="w-4 h-4" /> {suspBusy ? "Saving…" : "Apply pause"}
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---- WTD Summary ----
const WTDSummary = ({ wtdData, crew }) => {
  if (!wtdData) return null;
  
  const rate = wtdData.week_capture_rate || 0;
  const goalRate = wtdData.goal_rate || 0.35;
  const atGoal = wtdData.at_goal || false;
  
  return (
    <div className="relative rounded-3xl bg-white border border-slate-200/70 shadow-md ring-1 ring-slate-100 overflow-hidden mb-6" data-testid="wtd-summary">
      <div className="absolute inset-x-0 top-0 h-1 pointer-events-none" style={{ background: "linear-gradient(90deg, #0066FF 0%, #06b6d4 50%, #10b981 100%)" }} />
      <div className="px-4 sm:px-5 pt-4 pb-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2.5">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-2xl text-white shadow-md shrink-0" style={{ background: "linear-gradient(135deg, #0066FF, #06b6d4)" }}>
            <TrendUp weight="fill" className="w-4 h-4" />
          </span>
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-black text-blue-600 leading-none">
              Mission Control
            </p>
            <h3 className="text-lg font-black text-slate-900 leading-tight mt-1" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
              Week-to-Date
            </h3>
          </div>
        </div>
        <span className="text-[11px] font-bold text-slate-500">
          {formatDateUS(wtdData.week_start)} → {formatDateUS(wtdData.week_end)} ({wtdData.days_tracked} day{wtdData.days_tracked !== 1 ? "s" : ""})
        </span>
      </div>

      <div className="px-4 sm:px-5 pt-4 pb-2 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-2xl px-3 py-3 bg-gradient-to-br from-blue-50 to-indigo-50 ring-1 ring-blue-100">
          <p className="text-[10px] uppercase tracking-wider text-blue-700 font-black">WTD Transactions</p>
          <p className="text-2xl font-black tabular-nums text-slate-900 mt-1" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>{wtdData.week_transactions}</p>
        </div>
        <div className="rounded-2xl px-3 py-3 bg-gradient-to-br from-violet-50 to-fuchsia-50 ring-1 ring-violet-100">
          <p className="text-[10px] uppercase tracking-wider text-violet-700 font-black">WTD Emails</p>
          <p className="text-2xl font-black tabular-nums text-slate-900 mt-1" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>{wtdData.week_emails}</p>
        </div>
        <div className="rounded-2xl px-3 py-3 bg-gradient-to-br from-teal-50 to-cyan-50 ring-1 ring-teal-100">
          <p className="text-[10px] uppercase tracking-wider text-teal-700 font-black">WTD Capture Rate</p>
          <p className={`text-2xl font-black tabular-nums mt-1 ${atGoal ? "text-emerald-600" : "text-pink-600"}`} style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
            {(rate * 100).toFixed(1)}%
          </p>
        </div>
        <div className={`rounded-2xl px-3 py-3 ring-1 ${atGoal ? "bg-gradient-to-br from-emerald-50 to-teal-50 ring-emerald-200" : "bg-gradient-to-br from-pink-50 to-rose-50 ring-pink-200"}`}>
          <p className={`text-[10px] uppercase tracking-wider font-black ${atGoal ? "text-emerald-700" : "text-pink-700"}`}>WTD Status</p>
          <div className="flex items-center gap-1 mt-1.5">
            {atGoal ? (
              <><Check weight="fill" className="w-5 h-5 text-emerald-500" /><span className="font-black text-emerald-600">At Goal</span></>
            ) : (
              <><Warning weight="fill" className="w-5 h-5 text-pink-500" /><span className="font-black text-pink-600">Below Goal</span></>
            )}
          </div>
        </div>
      </div>

      {/* WTD Progress Bar */}
      <div className="px-4 sm:px-5 pt-2 pb-4">
        <div className="flex justify-between text-[11px] font-bold mb-1">
          <span className="text-slate-500">Goal: {(goalRate * 100).toFixed(0)}%</span>
          <span className={atGoal ? "text-emerald-600" : "text-pink-600"}>
            {(rate * 100).toFixed(1)}%
          </span>
        </div>
        <div className="progress-bar">
          <div
            className={`progress-bar-fill ${getProgressClass(atGoal, rate)}`}
            style={{ width: `${Math.min(100, (rate / goalRate) * 100)}%` }}
          />
        </div>
      </div>

      {/* WTD Crew Breakdown */}
      {wtdData.crew_wtd && wtdData.crew_wtd.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Crew WTD Emails</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {wtdData.crew_wtd.map((c) => (
              <div key={c.crew_id} className="flex items-center justify-between bg-slate-50 rounded-md px-3 py-2">
                <span className={`text-sm ${!c.active ? "text-slate-400" : ""}`}>
                  {c.first_name}{!c.active && " *"}
                </span>
                <span className="font-bold text-sm" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>{c.wtd_emails}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Heart Balloon top 5 leaderboard removed 2026-05-19 with the campaign cleanup. */}
      <div className="mt-4">
        {/* Heart Balloons inline leaderboard removed 2026-05-19 with the campaign cleanup. */}
      </div>
    </div>
  );
};

// ---- Goal Settings ----
export const GoalSettings = ({ settings, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [goalRate, setGoalRate] = useState("");
  const [stretchRate, setStretchRate] = useState("");

  const handleSave = () => {
    const updates = {};
    const g = parseFloat(goalRate);
    const s = parseFloat(stretchRate);
    if (!isNaN(g) && g > 0 && g <= 100) updates.goal_rate = g / 100;
    if (!isNaN(s) && s > 0 && s <= 100) updates.stretch_goal_rate = s / 100;
    if (Object.keys(updates).length) {
      onUpdate(updates);
      setEditing(false);
    }
  };

  if (!editing) {
    return (
      <button
        className="text-xs hover:opacity-75 underline"
        style={{ color: "var(--fb-blue)" }}
        onClick={() => {
          setGoalRate(((settings?.goal_rate || 0.35) * 100).toFixed(0));
          setStretchRate(((settings?.stretch_goal_rate || 0.50) * 100).toFixed(0));
          setEditing(true);
        }}
        data-testid="edit-goals-button"
      >
        Edit Goals
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2 mt-2" data-testid="goal-settings-form">
      <div className="flex items-center gap-1">
        <span className="text-xs text-slate-500">Goal:</span>
        <input type="number" value={goalRate} onChange={(e) => setGoalRate(e.target.value)} className="w-14 h-7 text-center border border-slate-200 rounded text-xs font-semibold" data-testid="goal-rate-input" />
        <span className="text-xs text-slate-500">%</span>
      </div>
      <div className="flex items-center gap-1">
        <span className="text-xs text-slate-500">Stretch:</span>
        <input type="number" value={stretchRate} onChange={(e) => setStretchRate(e.target.value)} className="w-14 h-7 text-center border border-slate-200 rounded text-xs font-semibold" data-testid="stretch-rate-input" />
        <span className="text-xs text-slate-500">%</span>
      </div>
      <Button size="sm" onClick={handleSave} className="h-7 text-xs fb-btn-primary" data-testid="save-goals-button">Save</Button>
      <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-7 text-xs">Cancel</Button>
    </div>
  );
};

// ---- Store Hours Settings ----
// Per-day store hours — 7-day tabbed editor (plus a "Default" tab for the
// standard M–F hours). Days without an explicit override inherit the default
// times. Sunday early-close and other varying schedules drive unlock windows
// for checkpoint tabs + the late-reading nudge scheduler.
const DAY_META = [
  { key: "sun", label: "Sun", full: "Sunday" },
  { key: "mon", label: "Mon", full: "Monday" },
  { key: "tue", label: "Tue", full: "Tuesday" },
  { key: "wed", label: "Wed", full: "Wednesday" },
  { key: "thu", label: "Thu", full: "Thursday" },
  { key: "fri", label: "Fri", full: "Friday" },
  { key: "sat", label: "Sat", full: "Saturday" },
];
const DAY_HOURS_FIELDS = [
  { key: "open_time", label: "Doors Open" },
  { key: "cp1_time", label: "Reading 1" },
  { key: "cp2_time", label: "Reading 2" },
  { key: "cp3_time", label: "Reading 3" },
  { key: "close_time", label: "Store Closes" },
];

const todayDayKey = () => {
  // JS: Sun=0..Sat=6 → matches our DAY_META order exactly.
  try {
    const now = new Date();
    const mstStr = now.toLocaleString("en-US", { timeZone: "America/Denver", weekday: "short" });
    const key = mstStr.toLowerCase().slice(0, 3);
    return DAY_META.some((d) => d.key === key) ? key : "mon";
  } catch {
    return "mon";
  }
};

export const StoreHoursSettings = ({ settings, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [activeDay, setActiveDay] = useState("default"); // "default" or sun..sat
  const [defaults, setDefaults] = useState({});
  const [overrides, setOverrides] = useState({});

  // `effective(day)` returns the hours currently applicable to that day,
  // layering overrides on top of defaults. Single source of truth for both
  // the view mode and the edit form.
  const effective = useCallback(
    (dayKey) => {
      const base = {
        open_time: defaults.open_time || "09:00",
        cp1_time: defaults.cp1_time || "14:00",
        cp2_time: defaults.cp2_time || "17:00",
        cp3_time: defaults.cp3_time || "20:00",
        close_time: defaults.close_time || "21:00",
      };
      if (dayKey === "default") return base;
      const o = overrides[dayKey] || {};
      return { ...base, ...Object.fromEntries(Object.entries(o).filter(([, v]) => v)) };
    },
    [defaults, overrides],
  );

  const start = () => {
    setDefaults({
      open_time: settings?.open_time || "09:00",
      cp1_time: settings?.cp1_time || "14:00",
      cp2_time: settings?.cp2_time || "17:00",
      cp3_time: settings?.cp3_time || "20:00",
      close_time: settings?.close_time || "21:00",
    });
    setOverrides(settings?.day_hours_overrides || {});
    setActiveDay("default");
    setEditing(true);
  };

  const isValid = (t) => /^([01]\d|2[0-3]):[0-5]\d$/.test(t);

  // Helpers: HH:MM ↔ minutes. Used by the auto-rebalance logic below so a
  // change to open or close time cascades to the checkpoint reads — i.e.
  // if a SM moves their store close from 9:00 PM → 9:30 PM, the "Reading
  // 3" CP3 stamp also shifts to 1h before close (8:30 PM) instead of
  // stranding the schedule on the old 8:00 PM mark. (User report 2026-05-26
  // "if we change store hours for 930pm close, the checkpoints should
  // match the new times".)
  const parseHM = (s) => {
    if (!isValid(s)) return null;
    const [h, m] = s.split(":").map((n) => parseInt(n, 10));
    return h * 60 + m;
  };
  const fmtHM = (mins) => {
    const m = ((mins % 1440) + 1440) % 1440;
    return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
  };

  const updateCell = (dayKey, fieldKey, value) => {
    // Auto-balance the three reading checkpoints whenever open_time or
    // close_time changes. CP3 anchors at 1h before close (the EOD read).
    // CP1 / CP2 evenly divide the open → CP3 span so they always feel
    // proportionally spaced (instead of stranding 14:00 / 17:00 against
    // a brand-new 21:30 close that pushes CP3 out by 30 min).
    const rebalance = (hours, changedKey) => {
      if (changedKey !== "open_time" && changedKey !== "close_time") return hours;
      const open = parseHM(hours.open_time);
      const close = parseHM(hours.close_time);
      if (open == null || close == null || close <= open + 90) return hours;
      const cp3 = close - 60;            // 1h before close
      const cp1 = open + Math.round((cp3 - open) / 3);       // ~33% in
      const cp2 = open + Math.round(((cp3 - open) * 2) / 3); // ~66% in
      return { ...hours, cp1_time: fmtHM(cp1), cp2_time: fmtHM(cp2), cp3_time: fmtHM(cp3) };
    };
    if (dayKey === "default") {
      setDefaults((d) => rebalance({ ...d, [fieldKey]: value }, fieldKey));
      return;
    }
    setOverrides((prev) => {
      const next = { ...prev };
      const cur = { ...(next[dayKey] || {}) };
      cur[fieldKey] = value;
      // For per-day overrides we need the EFFECTIVE hours (override layered
      // over defaults) before rebalancing so missing fields don't blow up.
      const effHours = { ...defaults, ...Object.fromEntries(Object.entries(cur).filter(([, v]) => v)) };
      const rebalanced = rebalance(effHours, fieldKey);
      // Persist only the changed fields back to the override (keep it lean).
      if (fieldKey === "open_time" || fieldKey === "close_time") {
        cur.cp1_time = rebalanced.cp1_time;
        cur.cp2_time = rebalanced.cp2_time;
        cur.cp3_time = rebalanced.cp3_time;
      }
      next[dayKey] = cur;
      return next;
    });
  };

  const clearDayOverride = (dayKey) => {
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[dayKey];
      return next;
    });
  };

  const copyDefaultToDay = (dayKey) => {
    setOverrides((prev) => ({ ...prev, [dayKey]: { ...defaults } }));
  };

  const handleSave = () => {
    const defaultEntries = Object.entries(defaults);
    if (defaultEntries.some(([, v]) => !isValid(v))) {
      toast.error("Default hours must use 24-hour HH:MM format.");
      return;
    }
    for (const [dayKey, o] of Object.entries(overrides)) {
      for (const [k, v] of Object.entries(o || {})) {
        if (v && !isValid(v)) {
          toast.error(`${dayKey.toUpperCase()} ${k} is invalid.`);
          return;
        }
      }
    }
    // Drop fully-empty overrides so the backend stays clean.
    const cleanOverrides = Object.fromEntries(
      Object.entries(overrides)
        .map(([k, o]) => [k, Object.fromEntries(Object.entries(o || {}).filter(([, v]) => v))])
        .filter(([, o]) => Object.keys(o).length > 0),
    );
    onUpdate({ ...defaults, day_hours_overrides: cleanOverrides });
    setEditing(false);
  };

  const todayKey = todayDayKey();
  const todayLabel = DAY_META.find((d) => d.key === todayKey)?.full || "Today";
  const todayHours = effective(todayKey);
  const hasOverride = overrides && overrides[activeDay];
  const activeHours = effective(activeDay);

  return (
    <div className="dashboard-card" data-testid="store-hours-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock weight="bold" className="w-5 h-5" style={{ color: "var(--fb-blue)" }} />
          <h3 className="text-lg font-bold" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>Store Hours</h3>
        </div>
        {!editing && (
          <button onClick={start} className="text-xs underline hover:opacity-75" style={{ color: "var(--fb-blue)" }} data-testid="edit-hours-button">
            Edit Hours
          </button>
        )}
      </div>

      {!editing ? (
        <>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Today · {todayLabel}</p>
            {(settings?.day_hours_overrides?.[todayKey]) && (
              <span className="text-[9px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5" data-testid="today-override-badge">
                Override
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm" data-testid="store-hours-view">
            {DAY_HOURS_FIELDS.map(({ key, label }) => (
              <div key={key} className="bg-slate-50 rounded-md px-3 py-2">
                <p className="text-xs text-slate-500">{label}</p>
                <p className="font-bold" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>{formatHM(todayHours[key])}</p>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div data-testid="store-hours-form">
          {/* Day tabs — Default + 7 days */}
          <div className="flex gap-1 mb-3 overflow-x-auto -mx-1 px-1 pb-1 scrollbar-hide">
            <button
              type="button"
              onClick={() => setActiveDay("default")}
              className={`flex-shrink-0 px-3 py-1.5 rounded-md text-[11px] font-bold transition-colors ${activeDay === "default" ? "text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
              style={activeDay === "default" ? { backgroundColor: "var(--fb-blue)" } : {}}
              data-testid="hours-day-tab-default"
            >
              Default
            </button>
            {DAY_META.map((d) => {
              const isOverridden = !!overrides[d.key];
              const active = activeDay === d.key;
              return (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => setActiveDay(d.key)}
                  className={`relative flex-shrink-0 px-3 py-1.5 rounded-md text-[11px] font-bold transition-colors ${active ? "text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                  style={active ? { backgroundColor: "var(--fb-blue)" } : {}}
                  data-testid={`hours-day-tab-${d.key}`}
                >
                  {d.label}
                  {isOverridden && <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-white" aria-hidden />}
                </button>
              );
            })}
          </div>

          {activeDay !== "default" && (
            <div className="flex items-center justify-between mb-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
              <p className="text-[11px] text-slate-600">
                {hasOverride ? (
                  <>Overriding default for <strong>{DAY_META.find((d) => d.key === activeDay)?.full}</strong>.</>
                ) : (
                  <>Using default hours for <strong>{DAY_META.find((d) => d.key === activeDay)?.full}</strong>.</>
                )}
              </p>
              {hasOverride ? (
                <button type="button" onClick={() => clearDayOverride(activeDay)} className="text-[10px] font-semibold text-rose-600 hover:text-rose-800" data-testid="reset-day-override">
                  Reset to default
                </button>
              ) : (
                <button type="button" onClick={() => copyDefaultToDay(activeDay)} className="text-[10px] font-semibold text-blue-600 hover:text-blue-800" data-testid="start-day-override">
                  Customize this day
                </button>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {DAY_HOURS_FIELDS.map(({ key, label }) => {
              const editable = activeDay === "default" || hasOverride;
              const value = activeDay === "default" ? (defaults[key] || "") : (hasOverride ? ((overrides[activeDay] || {})[key] || activeHours[key]) : activeHours[key]);
              return (
                <div key={key} className="flex flex-col gap-1">
                  <label className="text-xs text-slate-500">{label}</label>
                  <input
                    type="time"
                    value={value}
                    disabled={!editable}
                    onChange={(e) => updateCell(activeDay, key, e.target.value)}
                    className={`h-9 border border-slate-200 rounded px-2 text-sm ${editable ? "" : "bg-slate-50 text-slate-400 cursor-not-allowed"}`}
                    data-testid={`hours-input-${activeDay}-${key}`}
                  />
                </div>
              );
            })}
          </div>

          <div className="flex gap-2 justify-end mt-3">
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-8 text-xs">Cancel</Button>
            <Button size="sm" onClick={handleSave} className="h-8 text-xs fb-btn-primary" data-testid="save-hours-button">Save Hours</Button>
          </div>
        </div>
      )}
      <p className="text-xs text-slate-400 mt-3">Crew shift clock, +1 tallying, and checkpoint tab unlock times follow today's schedule.</p>

      {/* ---- Closures ---- */}
      {/* Date-specific closures for holidays + emergencies. When today's date
          appears here, the entire Collect Emails page is disabled, tally
          endpoints return 423, and the EOD email is skipped. */}
      <StoreClosuresEditor settings={settings} onUpdate={onUpdate} />
    </div>
  );
};

// Sub-component to keep the parent readable. Lists upcoming + past closures,
// lets managers add/remove dates, and has a big "Close store TODAY" shortcut
// button for emergencies.
// Exported 2026-05-25 — SettingsPage now renders this directly for SMs so
// they keep emergency-close access even after the chain-wide hours panel
// got locked down to admin/DM only.
export const StoreClosuresEditor = ({ settings, onUpdate }) => {
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const closures = settings?.closed_dates || {};
  const todayMst = new Date().toLocaleDateString("en-CA", { timeZone: "America/Denver" });
  const isClosedToday = Boolean(closures[todayMst]);

  // Sort: future/today first (soonest next), then past (most recent first).
  const sorted = Object.entries(closures).sort(([a], [b]) => {
    const aFuture = a >= todayMst;
    const bFuture = b >= todayMst;
    if (aFuture && !bFuture) return -1;
    if (!aFuture && bFuture) return 1;
    return aFuture ? a.localeCompare(b) : b.localeCompare(a);
  });

  const save = (next) => onUpdate({ closed_dates: next });

  const handleAdd = () => {
    if (!date) { toast.error("Pick a date first."); return; }
    const trimmed = (reason || "").trim();
    if (!trimmed) { toast.error("Add a short reason (e.g. Thanksgiving, Flood)."); return; }
    save({ ...closures, [date]: trimmed });
    setDate(""); setReason("");
  };

  const handleRemove = (d) => {
    const next = { ...closures };
    delete next[d];
    save(next);
  };

  const closeToday = () => {
    const preset = closures[todayMst] || "Emergency closure";
    save({ ...closures, [todayMst]: preset });
  };

  const fmtDate = (d) => {
    try { const [y, m, day] = d.split("-"); return `${m}/${day}/${y.slice(2)}`; }
    catch { return d; }
  };

  return (
    <div className="mt-5 pt-4 border-t border-slate-200" data-testid="closures-section">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Lock weight="bold" className="w-4 h-4 text-rose-600" />
          <h4 className="text-sm font-bold" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>Closures</h4>
        </div>
        {isClosedToday ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-bold uppercase tracking-wider" data-testid="closed-today-badge">
            Closed today · {closures[todayMst]}
          </span>
        ) : (
          <Button
            size="sm"
            onClick={closeToday}
            className="h-7 text-[10px] bg-rose-600 hover:bg-rose-700 text-white font-bold uppercase tracking-wider"
            data-testid="close-today-button"
          >
            Close store today
          </Button>
        )}
      </div>

      {/* Add form */}
      <div className="grid grid-cols-[auto_1fr_auto] gap-2 items-end mb-2">
        <div>
          <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-0.5">Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-9 border border-slate-200 rounded px-2 text-sm"
            data-testid="closure-date-input"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-0.5">Reason</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Thanksgiving, Flood, Staff meeting…"
            className="h-9 border border-slate-200 rounded px-2 text-sm w-full"
            data-testid="closure-reason-input"
          />
        </div>
        <Button size="sm" onClick={handleAdd} className="h-9 text-xs fb-btn-primary" data-testid="closure-add-button">Add</Button>
      </div>

      {/* List */}
      {sorted.length === 0 ? (
        <p className="text-xs text-slate-400 italic" data-testid="closures-empty">No closures scheduled.</p>
      ) : (
        <ul className="divide-y divide-slate-100 border border-slate-100 rounded-md" data-testid="closures-list">
          {sorted.map(([d, r]) => {
            const isFuture = d >= todayMst;
            const isToday = d === todayMst;
            return (
              <li key={d} className={`flex items-center justify-between py-1.5 px-2.5 ${isToday ? "bg-rose-50" : ""}`} data-testid={`closure-${d}`}>
                <div>
                  <span className="font-bold text-sm" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>{fmtDate(d)}</span>
                  {isToday && <span className="ml-2 text-[10px] font-bold text-rose-700 uppercase">Today</span>}
                  {isFuture && !isToday && <span className="ml-2 text-[10px] font-bold text-slate-500 uppercase">Upcoming</span>}
                  <span className="block text-xs text-slate-600 mt-0.5">{r}</span>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(d)}
                  className="text-[10px] font-semibold text-slate-400 hover:text-rose-600 uppercase tracking-wider"
                  data-testid={`closure-remove-${d}`}
                  aria-label={`Remove closure on ${fmtDate(d)}`}
                >
                  Remove
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <p className="text-[10px] text-slate-400 mt-2">Closures block email tallies, checkpoint saves, and the EOD digest email for the selected date.</p>
    </div>
  );
};

// ---- Email Wins by Crew (read-only at-a-glance leaderboard) ----
// REMODELED 2026-06-13 per user feedback "should not feel like an
// editable form anymore. Less is more." Email counts are adjusted
// on the Zone Sheet now; this card is purely the Mission Control
// at-a-glance: who's carrying the day, total captured, who might
// need a push. Editable grid was removed; CTA deep-links to Zone
// Sheet where the inputs live.
//
// `crew` is the active roster (pre-scoped to the manager's store);
// `tallyData.crew_counts` is `{crew_id: count}` for today.
const CrewEmailAdjust = ({ crew, tallyData, onGoToZoneSheet }) => {
  const crewCounts = tallyData?.crew_counts || {};
  const totalCaptured = Object.values(crewCounts).reduce((a, b) => a + (Number(b) || 0), 0);
  const ranked = (crew || [])
    .map((m) => ({
      id: m.id,
      first_name: m.first_name || "Crew",
      count: Number(crewCounts[m.id]) || 0,
    }))
    .sort((a, b) => b.count - a.count);
  const topThree = ranked.filter((m) => m.count > 0).slice(0, 3);
  const carryingTheDay = topThree[0];
  const needsPush = ranked.filter((m) => m.count === 0).slice(0, 4);
  const medalGradient = ["linear-gradient(135deg, #f59e0b, #f97316)", "linear-gradient(135deg, #94a3b8, #64748b)", "linear-gradient(135deg, #b45309, #92400e)"];

  return (
    <div className="relative rounded-3xl bg-white border border-slate-200/70 shadow-md ring-1 ring-slate-100 overflow-hidden" data-testid="crew-email-adjust">
      <div className="absolute inset-x-0 top-0 h-1 pointer-events-none" style={{ background: "linear-gradient(90deg, #ec4899 0%, #f43f5e 50%, #f59e0b 100%)" }} />
      <div className="flex items-center justify-between px-4 sm:px-5 pt-4 pb-3 border-b border-slate-100 flex-wrap gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-2xl text-white shadow-md shrink-0" style={{ background: "linear-gradient(135deg, #ec4899, #f59e0b)" }}>
            <Trophy weight="fill" className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em] font-black text-pink-600 leading-none">
              Eyes on the Biz
            </p>
            <p className="text-lg font-black text-slate-900 leading-tight mt-1" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
              Email Wins by Crew
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onGoToZoneSheet}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-blue-50 text-[11px] sm:text-xs font-black text-blue-700 hover:bg-blue-100 transition-colors whitespace-nowrap"
          data-testid="crew-email-adjust-cta"
        >
          Adjust on Zone Sheet
          <ArrowsLeftRight weight="bold" className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="px-4 sm:px-5 py-4 space-y-4">
        {/* Total */}
        <div className="rounded-2xl px-4 py-3 bg-gradient-to-r from-pink-50 via-rose-50 to-amber-50 ring-1 ring-pink-100 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-black text-pink-700">Total Captured Today</p>
            <p className="text-3xl font-black tabular-nums text-slate-900 mt-1" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
              {totalCaptured}
            </p>
          </div>
          {carryingTheDay && (
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider font-black text-amber-700">Carrying the Day</p>
              <p className="text-base font-black text-slate-900 mt-1">
                {carryingTheDay.first_name} · {carryingTheDay.count}
              </p>
            </div>
          )}
        </div>

        {/* Podium */}
        {topThree.length > 0 ? (
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-black text-slate-500 mb-2">Top Collectors</p>
            <ul className="space-y-2">
              {topThree.map((m, idx) => (
                <li
                  key={m.id}
                  className="flex items-center justify-between rounded-2xl px-3 py-2 bg-slate-50 ring-1 ring-slate-200"
                  data-testid={`crew-rank-${m.id}`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span
                      className="inline-flex items-center justify-center w-7 h-7 rounded-full text-white text-[11px] font-black shadow-sm"
                      style={{ background: medalGradient[idx] || "linear-gradient(135deg, #64748b, #475569)" }}
                    >
                      {idx + 1}
                    </span>
                    <span className="text-sm font-black text-slate-900 truncate">{m.first_name}</span>
                  </div>
                  <span className="text-base font-black tabular-nums text-slate-700">
                    {m.count}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="rounded-2xl px-4 py-3 bg-slate-50 ring-1 ring-slate-200 text-sm font-bold text-slate-500 italic">
            No emails captured yet today. Let&apos;s start stacking wins.
          </div>
        )}

        {/* Needs a push */}
        {needsPush.length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-black text-pink-600 mb-2">
              Might Need a Push
            </p>
            <div className="flex flex-wrap gap-1.5">
              {needsPush.map((m) => (
                <span
                  key={m.id}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-pink-50 text-pink-700 text-[11px] font-black ring-1 ring-pink-200"
                  data-testid={`crew-needs-push-${m.id}`}
                >
                  {m.first_name}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ---- Shift Comparison ----
// Pure presentational: takes recap data, renders the 2pm → 5pm → 8pm comparison strip.
// Returns null when there isn't enough data to compare (<2 checkpoints).
export const ShiftComparison = ({ recapData }) => {
  if (!recapData || !recapData.shift_comparison || recapData.shift_comparison.length < 2) return null;

  const shifts = recapData.shift_comparison;

  return (
    <div className="dashboard-card" data-testid="shift-comparison">
      <div className="flex items-center gap-2 mb-4">
        <TrendUp weight="bold" className="w-5 h-5" style={{ color: "var(--fb-blue)" }} />
        <h3 className="text-lg font-bold" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>Shift Comparison</h3>
      </div>
      <div className="space-y-3">
        {shifts.map((shift, idx) => {
          const prevShift = idx > 0 ? shifts[idx - 1] : null;
          const rateDiff = prevShift ? (shift.rate_at_checkpoint - prevShift.rate_at_checkpoint) * 100 : 0;
          const trending = rateDiff > 0 ? "up" : (rateDiff < 0 ? "down" : "flat");

          const trendColorClass = {
            up: "text-emerald-600",
            down: "text-red-500",
            flat: "text-slate-500",
          }[trending];

          return (
            <div key={shift.checkpoint} className="flex items-center justify-between bg-slate-50 rounded-md p-3">
              <div>
                <p className="font-bold">{shift.checkpoint.toUpperCase()}</p>
                <p className="text-xs text-slate-500">
                  {shift.transactions} tx | {(shift.rate_at_checkpoint * 100).toFixed(1)}%
                  {shift.updated_by && <span className="ml-1">by {shift.updated_by}</span>}
                </p>
              </div>
              {prevShift && (
                <div className={`text-sm font-bold ${trendColorClass}`}>
                  {trending === "up" ? "+" : ""}{rateDiff.toFixed(1)}%
                  <span className="text-xs font-normal ml-1">
                    {trending === "up" ? "from " : trending === "down" ? "from " : "same as "}{prevShift.checkpoint.toUpperCase()}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Self-fetching wrapper — used on the Collect Emails page (and anywhere else that
// wants to show the live 2pm/5pm/8pm trend without its parent plumbing the
// recap fetch through. Quietly returns null when there aren't yet two
// checkpoints to compare for `date`.
export const ShiftComparisonCard = ({ date, refreshKey = 0 }) => {
  const [recap, setRecap] = useState(null);

  useEffect(() => {
    if (!date) return undefined;
    let cancelled = false;
    const load = () => {
      axios.get(`${API}/daily-recap/${date}`)
        .then((r) => { if (!cancelled) setRecap(r.data); })
        .catch(() => { /* silent — widget just won't render */ });
    };
    load();
    // Poll every 30s while visible so the card trends forward as new
    // checkpoints are logged. Paused when the tab is hidden.
    // (Battery fix 2026-08-01.)
    let iv = null;
    const start = () => {
      if (iv) return;
      iv = setInterval(() => { if (!document.hidden) load(); }, 30_000);
    };
    const stop = () => { if (iv) { clearInterval(iv); iv = null; } };
    const onVis = () => { if (document.hidden) stop(); else { load(); start(); } };
    start();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [date, refreshKey]);

  return <ShiftComparison recapData={recap} />;
};

// ---- Daily Recap ----
// Exported so the Reports page ("Recap" tab) can reuse this card for any past date
// without duplicating the end-of-day summary UI.
export const DailyRecap = ({ recapData }) => {
  if (!recapData || !recapData.has_8pm) return null;

  const rate = recapData.capture_rate || 0;
  const atGoal = recapData.at_goal || false;
  const top = recapData.top_performer;
  const bests = recapData.personal_bests || [];

  return (
    <div className={`dashboard-card border-2 ${atGoal ? "border-emerald-300 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid="daily-recap">
      <div className="flex items-center gap-2 mb-3">
        <Trophy weight="bold" className={`w-5 h-5 ${atGoal ? "text-emerald-600" : "text-amber-600"}`} />
        <h3 className="text-lg font-bold" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
          End of Day Recap
        </h3>
      </div>
      <div className="space-y-2">
        <p className="text-lg">
          <span className="font-black" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>{(rate * 100).toFixed(1)}%</span> capture rate
          {atGoal ? (
            <span className="ml-2 text-emerald-700 font-bold">- Goal Hit!</span>
          ) : (
            <span className="ml-2 text-amber-700 font-bold">- Below Goal</span>
          )}
        </p>
        <p className="text-sm text-slate-600">
          {recapData.total_emails} emails from {recapData.total_transactions} transactions | {recapData.crew_count} crew working
        </p>
        {top && (
          <p className="text-sm font-semibold">
            Top performer: <span style={{ color: "var(--fb-blue)" }}>{top.name}</span> with <span className="font-bold">{top.count}</span> emails
          </p>
        )}
        {bests.length > 0 && (
          <div className="mt-2 pt-2 border-t border-slate-200">
            <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1">Personal Bests Today!</p>
            {bests.map((b) => (
              <p key={b.crew_id} className="text-sm text-blue-700 font-medium">
                {b.first_name}: {b.count} emails (previous best: {b.previous_best})
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ---- Leaderboard ----
// A fun, photo-forward leaderboard. Top 3 get a "podium" card row with avatar,
// crown / medals and a glow; ranks 4+ get a compact list row.
//
// `managerMode` (default false) gates coaching-only details: the Strong/Coaching
// status badge and the click-through to CrewReportCard. Wow crew see a clean
// leaderboard focused on standings; managers get the coaching tools.
export const Leaderboard = ({ performance, onCrewClick, managerMode = false, limit = 0 }) => {
  // `limit` caps how many rows render (0 = no cap). The Wow Crew leaderboard
  // sub-tab passes `limit={10}` so the "Your Store" board stays focused on
  // the top of the team; the Manager Dashboard + standalone Leaderboard page
  // pass the default (no cap) so managers can see everyone for coaching.
  const fullList = performance || [];
  const list = limit > 0 ? fullList.slice(0, limit) : fullList;

  // Rank decoration: #1 = crown on gold, #2 = silver medal, #3 = bronze medal.
  const rankDecor = (idx) => {
    if (idx === 0) return { Icon: Crown, bg: "from-amber-300 to-yellow-500", ring: "ring-amber-300", glow: "shadow-[0_0_25px_rgba(251,191,36,0.45)]", label: "Champ" };
    if (idx === 1) return { Icon: Medal, bg: "from-slate-300 to-slate-500", ring: "ring-slate-300", glow: "shadow-[0_0_20px_rgba(148,163,184,0.35)]", label: "Runner-up" };
    if (idx === 2) return { Icon: Medal, bg: "from-orange-300 to-orange-600", ring: "ring-orange-300", glow: "shadow-[0_0_18px_rgba(251,146,60,0.3)]", label: "Bronze" };
    return null;
  };

  const Avatar = ({ member, size = 40 }) => {
    const initial = (member.first_name || "?").charAt(0).toUpperCase();
    // "Online" = heartbeat within the last 5 minutes. The heartbeat lives on
    // /api/notifications/unread-count which every signed-in user polls.
    const isOnline = (() => {
      const t = member.last_active_at;
      if (!t) return false;
      const ms = Date.parse(t);
      if (Number.isNaN(ms)) return false;
      return Date.now() - ms < 5 * 60 * 1000;
    })();
    // Dot is sized relative to the avatar so it looks right on both podium (48px)
    // and compact (32px) rows.
    const dotSize = Math.max(10, Math.round(size * 0.28));
    const dot = isOnline && (
      <span
        className="absolute rounded-full ring-2 ring-white bg-emerald-500"
        style={{
          width: dotSize,
          height: dotSize,
          right: -1,
          bottom: -1,
          boxShadow: "0 0 0 0 rgba(16,185,129,0.7)",
          animation: "fbPulseOnline 2s infinite",
        }}
        title={`Active just now`}
        data-testid={`crew-online-${member.crew_id}`}
      />
    );
    if (member.photo_url) {
      return (
        <span className="relative inline-block" style={{ width: size, height: size }}>
          <img
            src={member.photo_url}
            alt={member.first_name}
            className="rounded-full object-cover ring-2 ring-white shadow"
            style={{ width: size, height: size }}
          />
          {dot}
        </span>
      );
    }
    return (
      <span className="relative inline-block" style={{ width: size, height: size }}>
        <span
          className="rounded-full flex items-center justify-center text-white ring-2 ring-white shadow font-black"
          style={{
            width: size,
            height: size,
            backgroundColor: "var(--fb-blue, var(--fb-blue))",
            fontFamily: "'Five Below Gothic', Chivo, sans-serif",
            fontSize: Math.round(size * 0.42),
          }}
        >
          {initial}
        </span>
        {dot}
      </span>
    );
  };

  return (
    <div className="dashboard-card" data-testid="leaderboard">
      <div className="flex items-center gap-2 mb-4">
        <ChartLine weight="bold" className="w-5 h-5 text-slate-600" />
        <h3 className="text-lg font-bold" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
          Crew Leaderboard (7 Days)
        </h3>
        <span className="ml-auto flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
          <span className="w-2 h-2 rounded-full bg-emerald-500 ring-1 ring-emerald-200" /> Active now
        </span>
      </div>

      {list.length === 0 ? (
        <p className="text-slate-500 text-center py-8">No performance data yet</p>
      ) : (
        <div className="space-y-2">
          {list.map((m, idx) => {
            const decor = rankDecor(idx);
            const onClick = managerMode && onCrewClick ? () => onCrewClick(m.crew_id) : undefined;
            const RowTag = onClick ? "button" : "div";
            const rowInteractive = !!onClick;

            if (decor) {
              // Podium row (top 3)
              return (
                <RowTag
                  key={m.crew_id}
                  onClick={onClick}
                  className={`leaderboard-row w-full flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-white to-slate-50 border border-slate-200 ${rowInteractive ? "hover:border-slate-300 transition-all active:scale-[0.99] cursor-pointer" : "cursor-default"} text-left ${idx === 0 && rowInteractive ? "hover:shadow-md" : ""}`}
                  data-testid={`crew-report-link-${m.crew_id}`}
                >
                  {/* Rank crest */}
                  <div className={`relative flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br ${decor.bg} flex items-center justify-center ${decor.glow}`}>
                    <decor.Icon weight="fill" className="w-6 h-6 text-white drop-shadow" />
                    <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-white text-[10px] font-black flex items-center justify-center shadow ring-1 ring-slate-200">
                      {idx + 1}
                    </span>
                  </div>

                  {/* Avatar */}
                  <div className={`relative ring-2 ${decor.ring} rounded-full`}>
                    <Avatar member={m} size={48} />
                  </div>

                  {/* Name + meta */}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold truncate" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
                      {m.first_name}
                      {!m.active && <span className="text-xs text-slate-400 ml-1 font-normal">(Inactive)</span>}
                    </p>
                    <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{decor.label} • {m.avg_per_day.toFixed(1)}/day</p>
                  </div>

                  {/* Emails + status (status badge is coaching-only) */}
                  <div className="text-right flex-shrink-0">
                    <p className="text-2xl font-black leading-none" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>{m.total_emails}</p>
                    {managerMode && (
                      <span className={`status-badge mt-1 inline-block ${m.status === "strong" ? "strong" : "needs-coaching"}`}>
                        {m.status === "strong" ? "Strong" : "Coaching"}
                      </span>
                    )}
                  </div>
                </RowTag>
              );
            }

            // Ranks 4+ — compact row
            return (
              <RowTag
                key={m.crew_id}
                onClick={onClick}
                className={`leaderboard-row w-full flex items-center gap-3 px-3 py-2 rounded-lg ${rowInteractive ? "hover:bg-slate-50 transition-colors cursor-pointer" : "cursor-default"} text-left`}
                data-testid={`crew-report-link-${m.crew_id}`}
              >
                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold flex-shrink-0 ${getRankBadgeClass(idx)}`}>{idx + 1}</span>
                <Avatar member={m} size={32} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">
                    {m.first_name}
                    {!m.active && <span className="text-xs text-slate-400 ml-1">(Inactive)</span>}
                  </p>
                  <p className="text-xs text-slate-500">{m.avg_per_day.toFixed(1)}/day</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-bold" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>{m.total_emails}</p>
                  {managerMode && (
                    <span className={`status-badge ${m.status === "strong" ? "strong" : "needs-coaching"}`}>
                      {m.status === "strong" ? "Strong" : "Coaching"}
                    </span>
                  )}
                </div>
              </RowTag>
            );
          })}
          {limit > 0 && fullList.length > list.length && (
            <p className="text-[11px] text-slate-400 font-semibold text-center pt-2" data-testid="leaderboard-overflow-note">
              Showing top {list.length} · {fullList.length - list.length} more not shown
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// ---- Notifications Panel ----

const weekStartSundayLocal = (ymd) => {
  const d = new Date(ymd + "T00:00:00");
  d.setDate(d.getDate() - (d.getDay() % 7));
  return d.toISOString().slice(0, 10);
};
/**
 * CheckpointsMovedBanner — temporary post-launch nudge after the
 * 2026-06-12 relocation of the 2pm/5pm/8pm reading entry form from
 * the Manager Dashboard's Checkpoint tile to the Zone Sheet's
 * Eyes-on-the-Biz tile. Managers were hunting for where the readings
 * went; this banner explains it AND links them straight there. If a
 * slot is currently overdue, the banner upgrades into a stronger
 * "Missing 5 PM Reading — Log Now" CTA with the slot deep-linked.
 * Dismiss state persists per-user in localStorage so the banner
 * stops nagging after the first acknowledgement.
 */
const CheckpointsMovedBanner = ({ user, notifications, selectedDate, onNavigate }) => {
  const DISMISS_KEY = `wow_checkpoints_moved_v1_${user?.crew_id || "anon"}`;
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });
  if (dismissed) return null;
  // Surface the strongest action — if a slot is overdue right now,
  // link to that slot. Otherwise just open Eyes on the Biz at the top.
  const missed = (notifications || []).filter(
    (n) => n.type === "checkpoint_reminder" && !n.read && n.id && n.id.includes(selectedDate)
  );
  // 2026-02 layout pass — the generic "readings moved" banner has
  // been up for 8 months. Feature is stable and every manager knows
  // by now. Only render when a slot is ACTUALLY overdue right now —
  // otherwise the banner is pure noise on every dashboard load.
  // Preserves the "Missing X Reading — Log Now" upgrade path but
  // kills the persistent generic reminder.
  if (missed.length === 0) return null;
  const targetSlot = (() => {
    if (missed.length === 0) return "";
    const n = missed[0];
    if (n.slot) return n.slot;
    // id format: cp_reminder_{date}_{slot}_{sid}
    const parts = String(n.id || "").split("_");
    const idx = parts.indexOf("reminder");
    return idx >= 0 && parts[idx + 2] ? parts[idx + 2] : "";
  })();
  const goZoneSheet = () => {
    try {
      sessionStorage.setItem(
        "wow:openCheckpoint",
        JSON.stringify({ slot: targetSlot, date: selectedDate, store_id: user?.home_store_id || "" }),
      );
    } catch { /* quota */ }
    if (typeof onNavigate === "function") onNavigate("zone-sheet");
  };
  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* quota */ }
    setDismissed(true);
  };
  const slotPretty = targetSlot
    ? (targetSlot.toLowerCase() === "8pm" ? "Final" : targetSlot.toUpperCase().replace(/(\d+)PM/, "$1 PM"))
    : "";
  return (
    <div
      className="mb-4 rounded-2xl bg-slate-900 text-white shadow-md overflow-hidden"
      data-testid="checkpoints-moved-banner"
    >
      <div className="flex items-stretch">
        <button
          type="button"
          onClick={goZoneSheet}
          className="flex-1 text-left px-4 py-3 hover:bg-slate-800 transition-colors"
          data-testid="checkpoints-moved-cta"
        >
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-amber-300 mb-0.5">
            {targetSlot ? `Missing ${slotPretty} Reading` : "Checkpoint Readings Moved"}
          </p>
          <p className="text-sm font-bold leading-snug" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
            {targetSlot
              ? "Tap to log on Zone Sheet → Eyes on the Biz"
              : "2 PM, 5 PM, and Final readings are now entered on the Zone Sheet under Eyes on the Biz."}
          </p>
          <p className="text-xs font-bold text-amber-300 mt-1.5">
            Go to Zone Sheet →
          </p>
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="px-3 hover:bg-slate-800 transition-colors flex items-center justify-center"
          data-testid="checkpoints-moved-dismiss"
          aria-label="Dismiss"
        >
          <X weight="bold" className="w-4 h-4 text-slate-400" />
        </button>
      </div>
    </div>
  );
};



/**
 * Sticky red banner that pings managers (SM/MM/CEM/FTSL/PTSL) every
 * Sunday → through Saturday until they enter the week's WTD Plan $ +
 * LY Sales $. (User ask 2026-05-25 "weekly figures are being forgot
 * about because they are on the weekly tab, we need to prompt for
 * these on Sunday".) Auto-hides the moment both values land in
 * `db.weekly_ly`. Click scrolls the page to the Weekly Figures tile.
 */
const WeeklyFiguresBanner = ({ date, crewId, refreshKey, onNavigate }) => {
  const [needsEntry, setNeedsEntry] = useState(false);
  const weekStart = weekStartSundayLocal(date);
  // Sunday-only banner — Weekly Figures are a Sunday-setup item; the
  // banner would spam Mon-Sat even when the manager has no way to act
  // on it today. The figures still carry through the week from
  // whatever was entered Sunday. (User ask 2026-06-12 "Weekly Figures
  // should only show on Sundays".)
  const isSunday = (() => {
    try { return new Date((date || "") + "T00:00:00").getDay() === 0; } catch { return false; }
  })();
  useEffect(() => {
    // Skip the fetch entirely on non-Sundays — the banner is
    // hidden on Mon-Sat anyway (`if (!isSunday) return null` below).
    // Was firing every dashboard mount as wasted work. (Perf fix
    // 2026-06-18.)
    if (!isSunday) return undefined;
    let cancelled = false;
    const check = async () => {
      try {
        const r = await axios.get(`${API}/biz/weekly-ly/${weekStart}`);
        const plan = r.data?.plan_sales;
        const ly = r.data?.ly_sales;
        // Banner stays up until BOTH cells are populated (>0). A 0 entry
        // counts as "not entered" — we want a real number so the WTD
        // rollup math works.
        const missing = !(plan && Number(plan) > 0 && ly && Number(ly) > 0);
        if (!cancelled) setNeedsEntry(missing);
      } catch {
        if (!cancelled) setNeedsEntry(true);
      }
    };
    check();
    return () => { cancelled = true; };
  }, [weekStart, refreshKey, crewId, isSunday]);
  if (!isSunday) return null;
  if (!needsEntry) return null;
  const scrollToTile = () => {
    // Root-cause fix 2026-06-14: WeeklyFiguresCard is mounted on the
    // ZONE SHEET (inside Eyes on the Biz), NOT on the Manager Dashboard.
    // The orphaned import on the dashboard was left over from the
    // 2026-02-15 cleanup that moved entry to Zone Sheet but didn't
    // update this banner — querying [data-testid="weekly-figures-card"]
    // here returned null, so tapping ENTER did nothing.
    //
    // New behavior: drop a sessionStorage hint and navigate to the
    // Zone Sheet. ZoneSheetPage reads the hint on mount, dispatches
    // wow:openSection for Eyes on the Biz, and scrolls the weekly
    // figures card into view. Pattern mirrors the existing
    // wow:openCheckpoint deep-link.
    try {
      sessionStorage.setItem("wow:openWeeklyFigures", "1");
    } catch { /* quota */ }
    if (typeof onNavigate === "function") {
      onNavigate("zone-sheet");
      return;
    }
    // Fallback for legacy mount contexts (no onNavigate prop) — hard
    // route. Better than the previous no-op.
    if (typeof window !== "undefined") {
      window.location.href = "/?tab=zone-sheet";
    }
  };
  return (
    <div className="mb-4" data-testid="weekly-figures-banner">
      <button
        type="button"
        onClick={scrollToTile}
        className="w-full text-left bg-rose-50 border-2 border-rose-300 hover:bg-rose-100 transition-colors rounded-xl p-4 flex items-center gap-3"
        data-testid="weekly-figures-banner-cta"
      >
        <div className="w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center flex-shrink-0">
          <Warning weight="fill" className="w-5 h-5 text-rose-700" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-black text-rose-900 leading-tight">
            Weekly figures aren't in for this week
          </p>
          <p className="text-xs text-rose-700/80 mt-0.5">
            WTD Plan $ + LY Sales $ are missing — every EOD email + WTD rollup uses these. Tap to enter.
          </p>
        </div>
        <span className="text-[10px] font-black uppercase tracking-wider text-rose-700 whitespace-nowrap">Enter →</span>
      </button>
    </div>
  );
};

// ---- Notifications Panel ----

// PIN-reset notification row — inline Approve/Deny so a manager can resolve
// the request straight from the bell. (User 2026-06-12: "I'm not seeing the
// Cindy in reset request in my notifications bell that should be clickable
// to approve or deny".)
function PinResetNotifRow({ notif, actorId, onActed, currentUser }) {
  const [busy, setBusy] = useState(false);
  const [denyMode, setDenyMode] = useState(false);
  const [reason, setReason] = useState("");
  // Local resolved state for the ACTING manager. The SSE handler on
  // OTHER manager devices stamps `notif._resolved`; on the actor's own
  // device we set this local flag so the "Approved by <me>" trail is
  // consistent across every device that saw the request.
  const [localResolved, setLocalResolved] = useState(null);
  const initials = (notif.request_crew_name || "?").trim().charAt(0).toUpperCase();
  // Resolved-state (2026-08-30) — when another manager approves/denies
  // this same request, the SSE handler stamps `_resolved` on the notif
  // (rather than removing the row) so we render a permanent "Approved
  // by Dom · 2:30 PM" trail for the on-shift audience. Row auto-fades
  // after ~8s via the parent's timer. Buttons disable so nobody can
  // race-tap a stale action.
  const resolved = notif._resolved || localResolved || null;
  const act = async (kind) => {
    if (!actorId || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/auth/pin-reset/${notif.request_id}/${kind}`, {
        actor_id: actorId,
        reason: kind === "deny" ? reason.trim() : "",
      });
      setLocalResolved({
        resolution: kind === "approve" ? "approved" : "denied",
        by: (currentUser?.first_name) || "you",
        at: new Date().toISOString(),
      });
      // Delay the parent refresh so the manager sees the "Approved
      // by [me]" trail for a beat before the panel re-fetches and
      // the row drops off. Same 8s cadence used by the SSE fade
      // timer for consistency across devices.
      window.setTimeout(() => { if (onActed) onActed(); }, 8000);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || `Couldn't ${kind}`);
      setBusy(false);
    }
  };
  return (
    <div
      className={`w-full p-3 rounded-md mb-2 border-l-4 ${
        resolved
          ? (resolved.resolution === "approved"
              ? "bg-emerald-50 border-emerald-500"
              : "bg-slate-50 border-slate-400 opacity-90")
          : "bg-amber-50 border-amber-500"
      }`}
      data-testid={`notif-row-${notif.id}`}
    >
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-full bg-amber-100 overflow-hidden flex items-center justify-center text-sm font-black text-amber-900 flex-shrink-0">
          {notif.request_crew_photo
            ? <img src={notif.request_crew_photo} alt={notif.request_crew_name} className="w-full h-full object-cover" />
            : initials}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-slate-900 truncate">{notif.request_crew_name}</p>
          <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">
            {notif.request_crew_position || "Wow Crew"} · forgot PIN
          </p>
        </div>
      </div>
      {resolved ? (
        <div
          className={`mt-2 px-2 py-1.5 rounded-md text-[11px] font-bold inline-flex items-center gap-1.5 ${
            resolved.resolution === "approved"
              ? "bg-emerald-100 text-emerald-800"
              : "bg-slate-200 text-slate-700"
          }`}
          data-testid={`notif-pin-reset-resolved-${notif.id}`}
        >
          <Check weight="bold" className="w-3 h-3" />
          {resolved.resolution === "approved" ? "Approved" : "Denied"} by {resolved.by || "another manager"}
          {resolved.at ? ` · ${new Date(resolved.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : ""}
        </div>
      ) : denyMode ? (
        <div className="mt-2 flex items-center gap-1.5">
          <input
            type="text"
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, 80))}
            placeholder="Reason (optional)"
            className="flex-1 text-xs px-2 h-8 rounded-lg ring-1 ring-rose-200 focus:ring-rose-400 outline-none"
            onKeyDown={(e) => { if (e.key === "Enter") act("deny"); }}
            data-testid={`notif-pin-reset-deny-reason-${notif.id}`}
          />
          <button
            type="button"
            onClick={() => act("deny")}
            disabled={busy}
            className="h-8 px-2.5 rounded-lg bg-rose-600 text-white text-[11px] font-black uppercase tracking-wider active:scale-95 disabled:opacity-50"
            data-testid={`notif-pin-reset-confirm-deny-${notif.id}`}
          >Confirm</button>
          <button
            type="button"
            onClick={() => { setDenyMode(false); setReason(""); }}
            className="h-8 w-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center"
            aria-label="Cancel deny"
          >
            <X weight="bold" className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div className="mt-2 flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => act("approve")}
            disabled={busy}
            className="flex-1 h-8 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-black uppercase tracking-wider active:scale-95 disabled:opacity-50"
            data-testid={`notif-pin-reset-approve-${notif.id}`}
          >
            {busy ? "…" : "Approve"}
          </button>
          <button
            type="button"
            onClick={() => setDenyMode(true)}
            disabled={busy}
            className="flex-1 h-8 rounded-lg bg-slate-200 hover:bg-rose-200 text-slate-700 hover:text-rose-700 text-[11px] font-black uppercase tracking-wider active:scale-95 transition-colors disabled:opacity-50"
            data-testid={`notif-pin-reset-deny-${notif.id}`}
          >
            Deny
          </button>
        </div>
      )}
      <p className="text-xs text-slate-400 mt-1.5">{new Date(notif.created_at).toLocaleString()}</p>
    </div>
  );
}


export const NotificationsPanel = ({ notifications, onClose, onMarkAllRead, onRowClick, appVersion, currentUser, onRefresh }) => {
  // Frosted-glass bell-anchored popover (2026-02-14 redesign). Drops down
  // from the header bell with a small caret pointing up toward it. Portal
  // to document.body so a parent `backdrop-filter` / `transform` can't
  // trap it (the bell button lives inside the header icon cluster which
  // historically had backdrop-blur and broke fixed positioning — see the
  // earlier bottom-sheet bug).
  const node = (
    <>
      {/* Scrim — tap-to-dismiss without a full dim curtain so the page
          still feels alive behind. */}
      <button
        type="button"
        aria-label="Close notifications"
        onClick={onClose}
        className="fixed inset-0 z-[148] bg-slate-900/20 animate-[fadeIn_140ms_ease-out]"
        style={{ animation: "fadeIn 140ms ease-out forwards" }}
        data-testid="notifications-scrim"
      />
      {/* Popover */}
      <div
        className="fixed z-[149] flex flex-col rounded-2xl shadow-2xl animate-[notifPopIn_220ms_cubic-bezier(0.16,1,0.3,1)_forwards]"
        style={{
          top: "calc(env(safe-area-inset-top, 0px) + 64px)",
          right: 12,
          width: "min(340px, calc(100vw - 24px))",
          maxHeight: "min(70vh, 560px)",
          background: "rgba(255, 255, 255, 0.78)",
          backdropFilter: "blur(24px) saturate(180%)",
          WebkitBackdropFilter: "blur(24px) saturate(180%)",
          border: "1px solid rgba(255, 255, 255, 0.55)",
        }}
        role="dialog"
        aria-modal="true"
        data-testid="notifications-panel"
      >
        {/* Caret pointing up at the bell — same glass color so it reads as
            a continuation of the panel. */}
        <span
          aria-hidden
          className="absolute -top-2 right-6 w-4 h-4 rotate-45"
          style={{
            background: "rgba(255, 255, 255, 0.78)",
            backdropFilter: "blur(24px) saturate(180%)",
            WebkitBackdropFilter: "blur(24px) saturate(180%)",
            borderTop: "1px solid rgba(255, 255, 255, 0.55)",
            borderLeft: "1px solid rgba(255, 255, 255, 0.55)",
          }}
        />
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/40">
          <h3 className="text-sm font-black uppercase tracking-wider text-slate-900" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
            Notifications
          </h3>
          <div className="flex items-center gap-1">
            <button
              onClick={onMarkAllRead}
              className="text-[10px] font-bold uppercase tracking-wider text-blue-700 hover:text-blue-900 px-2 py-1 rounded"
              data-testid="notif-mark-all-read"
            >
              Mark all read
            </button>
            <button onClick={onClose} className="p-1 rounded-full hover:bg-white/50" data-testid="notif-close">
              <X weight="bold" className="w-4 h-4 text-slate-700" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-2 py-2" style={{ fontFamily: "'Five Below Gothic', Chivo, -apple-system, BlinkMacSystemFont, sans-serif" }}>
          {notifications.length === 0 ? (
            <p className="text-center text-slate-500 py-10 text-sm">No notifications</p>
          ) : (
            // Collapse consecutive identical notifications into one row
            // with a "×N" counter. Fixes the 2026-02-27 report where 3
            // identical "asked you to enable push" rows piled up. Belt-
            // and-suspenders — the backend now de-dupes on send, but old
            // rows already in the DB should also render clean. Uses the
            // most-recent timestamp for the collapsed group and hides
            // the intermediate rows so the badge count still matches
            // (we don't mutate the source notifications array).
            (() => {
              const collapsed = [];
              for (const n of notifications) {
                const last = collapsed[collapsed.length - 1];
                const sameKind = last
                  && last.type === n.type
                  && (last.message || "") === (n.message || "");
                if (sameKind) {
                  last._count = (last._count || 1) + 1;
                  // Keep the newest timestamp on the top row.
                  if (new Date(n.created_at) > new Date(last.created_at)) {
                    last.created_at = n.created_at;
                  }
                  last._ids = [...(last._ids || [last.id]), n.id];
                } else {
                  collapsed.push({ ...n, _count: 1, _ids: [n.id] });
                }
              }
              return collapsed;
            })().map((n, idx) => {
              if (n.type === "pin_reset_request") {
                return (
                  <div key={n.id} style={{ animation: `notifRowIn 280ms ease-out ${Math.min(idx * 35, 240)}ms both` }}>
                    <PinResetNotifRow
                      notif={n}
                      actorId={currentUser?.crew_id}
                      currentUser={currentUser}
                      onActed={() => { if (onRefresh) onRefresh(); }}
                    />
                  </div>
                );
              }
              // Every notification is clickable — route by type in
              // handleNotificationClick, defaulting to Inbox for anything
              // without a dedicated destination (user ask 2026-06-09:
              // "all notifications need to be clickable to their
              // destination"). No more per-type allowlist that left
              // story/checkpoint/AI-win-plan/shift-end rows dead.
              const clickable = !!onRowClick;
              const Tag = clickable ? "button" : "div";
              return (
                <Tag
                  key={n.id}
                  type={clickable ? "button" : undefined}
                  onClick={clickable ? () => onRowClick(n) : undefined}
                  className={`w-full text-left p-3 rounded-xl mb-1.5 block transition-colors ${
                    n.read
                      ? "bg-white/55 hover:bg-white/75"
                      : "bg-blue-50/85 border-l-4 border-blue-600 hover:bg-blue-100/85"
                  } ${clickable ? "cursor-pointer active:scale-[0.99]" : ""}`}
                  style={{ animation: `notifRowIn 280ms ease-out ${Math.min(idx * 35, 240)}ms both` }}
                  data-testid={`notif-row-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    <p className="text-[14px] text-slate-800 leading-snug flex-1">{n.message}</p>
                    {n._count > 1 && (
                      <span
                        className="flex-shrink-0 text-[10px] font-black text-white bg-slate-500 px-1.5 py-0.5 rounded-full tabular-nums"
                        data-testid={`notif-count-${n.id}`}
                        title={`Received ${n._count} times`}
                      >
                        ×{n._count}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">
                    {new Date(n.created_at).toLocaleString()}
                  </p>
                </Tag>
              );
            })
          )}
        </div>
        {appVersion && (
          <div className="border-t border-white/40 px-4 py-2.5" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 10px)" }}>
            <p className="text-[10px] tracking-[0.14em] font-semibold text-slate-500" data-testid="notif-panel-app-version">
              v{appVersion}
            </p>
          </div>
        )}
      </div>
    </>
  );
  // Portal to body so a parent backdrop-filter / transform can't trap us.
  if (typeof document === "undefined") return node;
  return ReactDOM.createPortal(node, document.body);
};

// ---- Reorderable tile wrapper ----
const ReorderableTile = ({ id, index, total, editing, move, children, testid }) => {
  if (!editing) return children;
  return (
    <div className="relative group" data-testid={testid}>
      <div className="absolute top-2 right-2 z-10 flex items-center gap-1 bg-white/90 backdrop-blur border border-slate-200 rounded-md shadow-sm p-0.5">
        <button
          onClick={() => move(id, -1)}
          disabled={index === 0}
          className="p-1 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid={`move-up-${id}`}
          aria-label="Move up"
          title="Move up"
        >
          <CaretUp weight="bold" className="w-4 h-4" style={{ color: "var(--fb-blue)" }} />
        </button>
        <button
          onClick={() => move(id, 1)}
          disabled={index === total - 1}
          className="p-1 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid={`move-down-${id}`}
          aria-label="Move down"
          title="Move down"
        >
          <CaretDown weight="bold" className="w-4 h-4" style={{ color: "var(--fb-blue)" }} />
        </button>
      </div>
      <div className="ring-2 ring-blue-300 ring-offset-2 ring-offset-slate-50 rounded-lg animate-[pulse_3s_ease-in-out_infinite]">
        {children}
      </div>
    </div>
  );
};

// ---- MAIN MANAGER DASHBOARD ----
export default function ManagerDashboard({ user, onLogout, onSwitchToTally, onOpenInbox, onOpenSettings, onOpenReports, onGoHome, onNavigate }) {
  const [crew, setCrew] = useState([]);
  const [settings, setSettings] = useState(null);
  const [tallyData, setTallyData] = useState(null);
  const [checkpoints, setCheckpoints] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [wtdData, setWtdData] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [inboxUnread, setInboxUnread] = useState(0);
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [loading, setLoading] = useState(true);
  // Bump counter → every child of the dashboard that reads from
  // /api/biz/* will re-fetch when this number changes. Fires after every
  // checkpoint save so the "Eyes on the Biz" live preview tile reflects the
  // new Actual $ / WTD rollup without a page reload.
  const [bizRefreshKey, setBizRefreshKey] = useState(0);
  // Mission Control data — Wave A (User ask 2026-06-13). Fetched
  // alongside the existing dashboard payload so we do NOT add a new
  // poll cycle. Used by:
  //   • Win-The-Day Hero (store_focus)
  //   • Manager Handoff card (manager_notes + meta)
  //   • Who's on the Floor (crew_assignments[])
  //   • What's Fresh Today (store_focus_updated_at, manager_notes_updated_at)
  //   • Needs Attention (also reads `coverage-tasks/summary`)
  const [zoneSheet, setZoneSheet] = useState(null);
  const [coverageSummary, setCoverageSummary] = useState(null);
  // Wave A.5 — Today's Game Plan strip sources. Both endpoints already
  // exist (`/api/contest/may-2026` + `/api/events`); we just need to
  // surface them on the dashboard hero so the manager sees them
  // without hunting through the Contests tab or the calendar.
  const [contestSummary, setContestSummary] = useState(null);
  const [nextEvent, setNextEvent] = useState(null);
  // Wave A.6 — MTD aggregation for the 3-way hero toggle + last-shift
  // notes lookback for the Manager Handoff card.
  const [mtdData, setMtdData] = useState(null);
  const [lastShiftNotes, setLastShiftNotes] = useState(null);

  // Reorderable dashboard tiles — per-manager, persisted in localStorage.
  // Note: `leaderboard` now lives as its own HomePage tile, `daily_recap` moved
  // to the Reports → Recap tab, and `shift_comparison` moved to the Collect
  // Emails page (visible to everyone). None of those appear here anymore.
  // ─── REORDERABLE TILE GRID — REMAINING DEEP-DIVE TILES ────────────
  // After the 2026-06-14 dashboard reorganization the following tiles
  // were pulled OUT of the reorderable grid and rendered as standalone
  // cards in their new sections:
  //   • goal_calc      → Store Pulse
  //   • coverage_feed  → Game Plan
  //   • crew_adjust    → Recognition & Performance
  //
  // What's left in the grid is the "deeper data / config" set the
  // manager opens less often. Storage key bumped v6 → v7 to drop the
  // old saved ordering that referenced the extracted tiles.
  const DEFAULT_TILE_ORDER = ["wtd_financials", "wtd", "notes_history", "contests"];
  const tilesStorageKey = `mgr_tiles_v7_${user?.employee_id || "default"}`;
  const [tileOrder, setTileOrder] = useState(() => {
    try {
      const saved = localStorage.getItem(tilesStorageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length) {
          // Merge in any new tile ids that weren't in the saved order
          const missing = DEFAULT_TILE_ORDER.filter((id) => !parsed.includes(id));
          return [...parsed.filter((id) => DEFAULT_TILE_ORDER.includes(id)), ...missing];
        }
      }
    } catch (e) { /* ignore */ }
    return DEFAULT_TILE_ORDER;
  });
  // editingLayout is flipped on from Profile → Personal Preferences →
  // "Edit dashboard layout" (Wave A.6 2026-06-13). Profile sets
  // `localStorage.wow_edit_layout = "1"` then sends the manager back to
  // the dashboard. We read the flag on mount so the reorder controls
  // appear immediately without a hard refresh. Tapping "Done" here
  // clears the flag (see the Done handler below).
  const [editingLayout, setEditingLayout] = useState(() => {
    try { return localStorage.getItem("wow_edit_layout") === "1"; } catch { return false; }
  });
  // Active dashboard tab — Today / Week / Contests. Persists per-manager so
  // the dashboard reopens to whichever section they were last on. Default
  // is "week" after the 2026-06-14 reorg pulled the everyday tiles out
  // of the Today tab (Today is now an empty deep-dive slot reserved for
  // future "today-only" widgets a manager may opt into via Customize).
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const saved = localStorage.getItem(`mgr_tab_${user?.employee_id || "default"}`);
      if (saved && ["today", "week", "contests"].includes(saved)) return saved;
    } catch (e) { /* ignore */ }
    return "week";
  });

  const fetchData = useCallback(async () => {
    try {
      // Active store id — admin/DM can be mirroring another store, in
      // which case window.__wowActiveStoreId is set by apiScope.
      const sid = (typeof window !== "undefined" && window.__wowActiveStoreId)
        || user?.home_store_id || "";
      const actorId = user?.crew_id || "";
      const [crewRes, settingsRes, tallyRes, cpRes, notifRes, wtdRes, unreadRes, sheetRes, summaryRes, contestRes, eventsRes, mtdRes] = await Promise.all([
        axios.get(`${API}/crew-with-pins`, { params: { store_id: sid } }),
        axios.get(`${API}/settings`),
        axios.get(`${API}/tally/${selectedDate}`),
        axios.get(`${API}/checkpoints/${selectedDate}`),
        axios.get(`${API}/notifications?crew_id=${user?.crew_id || ""}&is_manager=true&limit=20`),
        axios.get(`${API}/wtd-summary`, { params: { date: selectedDate } }),
        user?.crew_id ? axios.get(`${API}/inbox/${user.crew_id}/unread-count?is_manager=true`) : Promise.resolve({ data: { unread: 0 } }),
        // Mission Control data sources — Wave A. Failures here MUST
        // NOT crash the dashboard (the existing email/checkpoint
        // workflow is the page's primary job). We catch each and
        // default to null so the new sections degrade gracefully.
        axios
          .get(`${API}/zone-sheets/${selectedDate}`, {
            params: { store_id: sid, actor_id: actorId },
          })
          .catch(() => ({ data: null })),
        axios
          .get(`${API}/zone-sheets/${selectedDate}/coverage-tasks/summary`, {
            params: { store_id: sid, actor_id: actorId },
          })
          .catch(() => ({ data: null })),
        // Today's Game Plan strip — contest + next event. Both are
        // existing endpoints; failures fall through to nothing (the
        // strip just hides the pill).
        axios.get(`${API}/contest/may-2026`).catch(() => ({ data: null })),
        axios
          .get(`${API}/events`, { params: { store_id: sid, actor_id: actorId } })
          .catch(() => ({ data: [] })),
        // MTD aggregation (Wave A.6 2026-06-13). Mirrors WTD shape so
        // the hero toggle can just swap data sources. Now anchored to
        // selectedDate (2026-02) so backwards navigation on the
        // DatePickerStrip picks the right month.
        axios.get(`${API}/mtd-summary`, { params: { date: selectedDate } }).catch(() => ({ data: null })),
        // Weekly Figures status — used by NeedsAttentionStrip's
        // weekly_missing row. Reads the SAME endpoint
        // (`/api/biz/weekly-ly/{weekStart}`) that WeeklyFiguresCard and
        // WeeklyFiguresBanner read, so all three surfaces can never
        // disagree on whether Plan/LY were entered. (User report
        // 2026-06-15 — pre-fix this row read `wtdData.actual_sales`,
        // a totally different field, and flagged stores that had
        // already entered weekly figures.)
        axios
          .get(`${API}/biz/weekly-ly/${weekStartSundayLocal(selectedDate)}`)
          .catch(() => ({ data: null })),
      ]);
      setCrew(crewRes.data);
      setSettings(settingsRes.data);
      setTallyData(tallyRes.data);
      setCheckpoints(cpRes.data);
      setNotifications(notifRes.data);
      setWtdData(wtdRes.data);
      setInboxUnread(unreadRes.data.unread || 0);
      setZoneSheet(sheetRes.data || null);
      setCoverageSummary(summaryRes.data || null);
      setContestSummary(contestRes.data || null);
      // Pick the earliest upcoming event (today or later). Events
      // endpoint returns them already sorted by date; defensive
      // sort + filter here so a future endpoint change doesn't
      // surface yesterday's event on the hero.
      const events = Array.isArray(eventsRes.data) ? eventsRes.data : [];
      const today = selectedDate;
      const upcoming = events
        .filter((e) => (e?.date || "") >= today)
        .sort((a, b) => String(a.date).localeCompare(String(b.date)));
      setNextEvent(upcoming[0] || null);
      setMtdData(mtdRes.data || null);

      // Paint the page NOW — the page is fully usable with the data
      // from the Promise.all batch above. The Last-Shift handoff
      // lookback below is non-blocking (it fills in <300ms after
      // initial paint via setState).  Previously this was sequential
      // and added up to ~600ms of dead waterfall after the parallel
      // batch landed, which the user perceived as "the dashboard
      // takes a couple of seconds to load". (Perf fix 2026-06-18.)
      setLoading(false);

      // Manager Handoff — "From Last Shift" lookback. Find the most
      // recent prior day (within 3) with non-empty `manager_notes`.
      // Parallelized 2026-06-18: all three days fire concurrently and
      // we pick the most-recent that has notes. Saves up to 200ms of
      // serial waterfall vs the previous day-1 → day-2 → day-3 loop.
      // Wrapped in its own try/catch so a failure here can't strand
      // the page on `loading=true` (the setLoading(false) above
      // already flipped it).
      (async () => {
        try {
          const todayDate = new Date(`${selectedDate}T12:00:00`);
          const priorDays = [1, 2, 3].map((i) => {
            const d = new Date(todayDate);
            d.setDate(todayDate.getDate() - i);
            return d.toISOString().slice(0, 10);
          });
          const priorResults = await Promise.all(
            priorDays.map((ymd) =>
              axios
                .get(`${API}/zone-sheets/${ymd}`, { params: { store_id: sid, actor_id: actorId } })
                .then((r) => ({ ymd, data: r.data }))
                .catch(() => ({ ymd, data: null }))
            )
          );
          // First (i.e. most recent) day in the array that has notes wins.
          const found = priorResults.find(
            (r) => r?.data && (r.data.manager_notes || "").trim()
          );
          setLastShiftNotes(found ? { date: found.ymd, ...found.data } : null);
        } catch {
          setLastShiftNotes(null);
        }
      })();
    } catch (e) {
      console.error("Fetch error:", e);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchData();
    // Poll every 10s while visible. Paused on hidden so a manager who
    // walks the floor with the app backgrounded isn't burning battery
    // hitting the dashboard endpoint over LTE. Fires immediately on
    // visibility-return so state is fresh when they come back.
    // (Battery fix 2026-08-01.)
    let interval = null;
    const start = () => {
      if (interval) return;
      interval = setInterval(() => { if (!document.hidden) fetchData(); }, 10000);
    };
    const stop = () => { if (interval) { clearInterval(interval); interval = null; } };
    const onVis = () => {
      if (document.hidden) stop();
      else { fetchData(); start(); }
    };
    start();
    document.addEventListener("visibilitychange", onVis);
    // Refetch IMMEDIATELY when admin enters/exits mirror mode — otherwise
    // the dashboard shows stale crew (the previous scope's roster) for up
    // to 10s until the next poll. (Bug fix 2026-05-22 — user report
    // "still showing all wow crew when mirroring 3010".)
    const onScope = () => fetchData();
    window.addEventListener("wow:scope-changed", onScope);
    // 2026-08-27 — When ANY manager approves/denies a PIN reset, the
    // backend fires an SSE `pin_reset_resolved` broadcast to every
    // other manager. STAMP the notification's `_resolved` field so
    // the row transforms into a permanent "Approved by [Name]"
    // subtext (audit trail for the on-shift audience) instead of
    // silently vanishing. A short auto-fade timer removes it after
    // ~8s so the panel doesn't accumulate stale rows all shift.
    const onNotif = (e) => {
      const evt = e?.detail || {};
      if (evt.kind === "pin_reset_resolved" && evt.request_id) {
        setNotifications((prev) => prev.map((n) =>
          (n?.type === "pin_reset_request" && n?.request_id === evt.request_id)
            ? { ...n, _resolved: {
                resolution: evt.resolution || "approved",
                by: evt.resolved_by || "another manager",
                at: evt.resolved_at || new Date().toISOString(),
              } }
            : n
        ));
        // After 8 seconds, fade the resolved row out of the panel so
        // it doesn't clutter the bell forever. Managers still see the
        // audit trail in the log; this is purely presentational.
        window.setTimeout(() => {
          setNotifications((prev) => prev.filter(
            (n) => !(n?.type === "pin_reset_request" && n?.request_id === evt.request_id)
          ));
        }, 8000);
        // Friendly toast so managers know it was handled + by whom.
        try {
          toast.success(
            `${evt.target_name || "PIN"} — ${evt.resolution === "approved" ? "approved" : "denied"} by ${evt.resolved_by || "another manager"}`
          );
        } catch { /* toast import guard */ }
      }
    };
    window.addEventListener("wow:notification", onNotif);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("wow:scope-changed", onScope);
      window.removeEventListener("wow:notification", onNotif);
    };
  }, [fetchData]);

  // Handlers
  const handleAdjustCrewEmail = async (crewId, date, count) => {
    try {
      await axios.put(`${API}/tally/adjust`, { 
        crew_id: crewId, 
        date, 
        count,
        adjusted_by_name: user?.first_name || "Manager",
        // Powers backend manager-only guard (security fix 2026-05-22).
        actor_id: user?.crew_id || ""
      });
      const member = crew.find(c => c.id === crewId);
      toast.success(`${member?.first_name}'s emails set to ${count}`);
      fetchData();
    } catch (e) { toast.error("Failed to adjust"); }
  };

  const handleCheckpoint = async (data) => {
    try {
      await axios.post(`${API}/checkpoints`, {
        ...data,
        updated_by_id: user?.crew_id || "",
        updated_by_name: user?.first_name || "Manager",
      });
      toast.success(`${data.checkpoint.toUpperCase()} saved · Eyes on the Biz updated`);
      setCheckpoints(prev => {
        const existing = prev.findIndex(cp => cp.checkpoint === data.checkpoint);
        const cpData = {
          checkpoint: data.checkpoint,
          store_transactions: data.store_transactions,
          date: data.date,
          updated_by_name: user?.first_name || "Manager",
          updated_at: new Date().toISOString()
        };
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = { ...updated[existing], ...cpData };
          return updated;
        }
        return [...prev, cpData];
      });
      fetchData();
    } catch (e) {
      toast.error("Failed");
      throw e;
    }
  };

  const handleClearCheckpoint = async (date, checkpoint) => {
    try {
      await axios.delete(`${API}/checkpoints/${date}/${checkpoint}`, {
        // actor_id powers the backend manager-only guard (matches the
        // create endpoint). Sent as a query param so the existing axios
        // store_id interceptor doesn't get confused with a body.
        params: { actor_id: user?.crew_id || "" },
      });
      toast.success(`${checkpoint.toUpperCase()} checkpoint cleared`);
      setCheckpoints(prev => prev.filter(cp => cp.checkpoint !== checkpoint));
      fetchData();
    } catch (e) { toast.error("Failed to clear checkpoint"); }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all?crew_id=${user?.crew_id || ""}&is_manager=true`);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (e) { console.error(e); }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;
  // tallyData KPIs (total_emails/store_transactions/capture_rate/at_goal)
  // are no longer read directly here — the duplicate 4-card stat grid
  // was removed 2026-06-13. The new Win-The-Day Hero +
  // EmailCaptureGlanceCard + GoalCalculator each read tallyData
  // themselves with the right framing, so we don't need the locals.

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="manager-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          {/* Top row: logo + name + dock. Single row at all widths —
              dock button sizing scales down on phones so the whole
              header fits without wrapping. */}
          <div className="flex items-center justify-between gap-2 min-h-14 py-2">
            <div className="flex items-center gap-2 min-w-0">
              <img src={settings?.logo_url || "/fivebelow-logo.png"} alt="Five Below" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
              <div className="min-w-0">
                <h1 className="text-base sm:text-xl font-bold truncate leading-tight" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", color: "var(--fb-blue)" }} data-testid="app-name">
                  {settings?.app_name || "Wow App"}
                </h1>
                <p
                  className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 leading-tight"
                  data-testid="page-label"
                >
                  Manager Dashboard
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
              <div className="hidden md:flex items-center gap-1 px-2 py-1 rounded-md" style={{ backgroundColor: "#DBEAFE" }}>
                <Lock weight="bold" className="w-4 h-4" style={{ color: "var(--fb-blue)" }} />
                <span className="text-sm font-medium" style={{ color: "var(--fb-blue)" }}>{user?.first_name || "Manager"}</span>
              </div>
              {/* One unified dock — primary nav + Logout action live
                  inside the same rounded pill. Replaces the previous
                  floating Home button + standalone Logout button. */}
              {(onGoHome || onNavigate) && (
                <QuickJumpDock
                  active="manager"
                  onGoHome={onGoHome}
                  onNavigate={onNavigate}
                  extras={onLogout ? [
                    { id: "logout", Icon: SignOut, label: "Sign out", onClick: onLogout, tone: "danger" },
                  ] : []}
                />
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
        {/* Date picker — single tap to open a real calendar with quick
            shortcuts ("Today", "Yesterday", "Sunday") for fast back-fill.
            Replaces the old read-only text field that had no way to pick
            a different date — user reported it was "mission impossible
            and ugly" 2026-02-22. */}
        <div className="flex flex-row items-center justify-between gap-3 mb-4 sm:mb-6">
          <DatePickerStrip selectedDate={selectedDate} onChange={setSelectedDate} />
          {/* Collect Emails moved into the header QuickJumpDock —
              the standalone button here was a duplicate entry point
              for the same destination. (Removed 2026-06-19.) */}
        </div>

        {/* Goal Streak — relocated below the Mission Control sections
            (Wave A 2026-06-13). The Win The Day Hero now answers the
            "are we winning today?" question; the streak chip is a
            secondary motivator and reads more naturally below the
            fold. */}

        {/* Checkpoints-moved relocation banner (2026-06-12 temporary).
            Managers were hunting for the 2pm / 5pm / 8pm reading entry
            form because it relocated from Manager Dashboard → Zone
            Sheet's Eyes on the Biz tile. One-shot dismissible banner
            so the team gets a clear cue. Dismissal persists per-user
            in localStorage. Manager-only — ManagerDashboard is gated
            on manager role at App.js. */}
        <CheckpointsMovedBanner
          user={user}
          notifications={notifications}
          selectedDate={selectedDate}
          onNavigate={onNavigate}
        />

        {/* Manager Messages */}
        {user?.crew_id && (
          <CrewMessageBanner crewId={user.crew_id} isManager={true} />
        )}

        {/* Weekly figures nudge (2026-05-25) — sticky red banner from
            Sunday onwards if the current retail week's WTD Plan $ + LY
            Sales $ haven't been entered yet. Taps scroll the page down
            to the Weekly Figures tile so the manager can enter both in
            two cells. Disappears the moment plan + ly are saved.
            Visible to anyone who can enter weekly figures — i.e. every
            manager-tier role (SM / MM / CEM / FTSL / PTSL). */}
        <WeeklyFiguresBanner date={selectedDate} crewId={user?.crew_id || ""} refreshKey={bizRefreshKey} onNavigate={onNavigate} />

        {/* Missed-reading nudge — one big red banner per overdue slot.
            Tap routes the manager to the Zone Sheet's Eyes on the Biz
            tile (where the actual reading entry form lives) and
            auto-selects the missing slot. Previously routed to the
            Manager Dashboard's Checkpoint tile which was the WRONG
            destination per the in-store workflow. (User correction
            2026-06-12 "Tap to enter on Zone Sheet" — not Manager
            Dashboard.) */}
        {(() => {
          const missed = (notifications || []).filter(
            (n) => (n.type === "checkpoint_reminder" || n.type === "eod_missing") && !n.read && n.id && n.id.includes(selectedDate)
          );
          if (missed.length === 0) return null;
          const slotFromId = (notif) => {
            if (notif.slot) return notif.slot;
            // id format: cp_reminder_{date}_{slot}_{sid}
            const parts = String(notif.id || "").split("_");
            const idx = parts.indexOf("reminder");
            return idx >= 0 && parts[idx + 2] ? parts[idx + 2] : "";
          };
          const openMissed = (notif) => {
            const slot = slotFromId(notif);
            // Stash a deep-link hint that ZoneSheetPage reads on
            // mount — scrolls the Eyes on the Biz tile into view and
            // switches the CheckpointEntry to the right slot.
            try {
              sessionStorage.setItem(
                "wow:openCheckpoint",
                JSON.stringify({ slot, date: notif.date || selectedDate, store_id: notif.store_id || "" }),
              );
            } catch { /* quota */ }
            if (typeof onNavigate === "function") {
              onNavigate("zone-sheet");
            } else {
              // Fallback when ManagerDashboard isn't given an onNavigate
              // prop (legacy mount contexts). Fire the event anyway so
              // a Zone Sheet that's already mounted picks it up.
              try {
                window.dispatchEvent(new CustomEvent("wow:openCheckpoint", { detail: { slot } }));
              } catch { /* ignore */ }
            }
          };
          return (
            <div className="mb-4 space-y-2" data-testid="missed-reading-banner">
              {missed.slice(0, 3).map((n) => (
                <Banner
                  key={n.id}
                  variant="alert"
                  icon={Warning}
                  title="Reading overdue"
                  onClick={() => openMissed(n)}
                  trailing={<span className="text-[10px] font-black uppercase tracking-wider text-rose-700 whitespace-nowrap">Log now →</span>}
                  testid={`banner-missed-${n.id}`}
                >
                  {n.message.replace(/^⏰\s*/, "")}
                </Banner>
              ))}
            </div>
          );
        })()}

        {/* ═══════════════════════════════════════════════════════════
            MANAGER DASHBOARD — sectioned workflow layout
            (Reorganization 2026-06-14 per user ask):
                1. Alerts & Action Required  — what needs attention?
                2. Hero (Win The Day)        — kept exactly where it was
                3. Store Pulse               — how is the business doing?
                4. Game Plan                 — who's working / on what?
                5. Manager Communication     — handoff context
                6. Operations                — truck / pallet plan
                7. Recognition & Performance — wins, contests, deep dives
                8. What's Fresh Today        — tail-of-page activity
            All blocks consume already-fetched data (no new endpoints).
            ═══════════════════════════════════════════════════════ */}

        {/* ── 1. Alerts & Action Required ─────────────────────────── */}
        <SectionHeader
          title="Alerts & Action Required"
          testid="section-alerts"
        />
        <NeedsAttentionStrip
          tallyData={tallyData}
          summary={coverageSummary}
          notes={zoneSheet?.manager_notes || ""}
          lastShiftNotes={lastShiftNotes}
          truckPlan={zoneSheet?.truck_plan || null}
          wtdData={wtdData}
          onGoToZoneSheet={() => {
            if (typeof onNavigate === "function") onNavigate("zone-sheet");
          }}
          onGoToTruckPlan={() => {
            // In-page scroll to the Operations section's TruckPlanCard.
            try {
              const el = document.querySelector('[data-testid="truck-plan-card"]');
              if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            } catch { /* ignore */ }
          }}
          onGoToWeeklyFigures={() => {
            // Weekly figures live inside the WTD Financials tile in the
            // Week tab — switch to that tab and scroll the page down.
            try {
              if (typeof window !== "undefined" && typeof window.__wowSwitchTab === "function") {
                window.__wowSwitchTab("week");
              }
              const el = document.querySelector('[data-testid="wtd-financials-tile"]');
              if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            } catch { /* ignore */ }
          }}
        />

        {/* ── 2. Hero — Win The Day (KEEP IN PLACE per user ask) ──── */}
        <div className="space-y-3 mt-3">
          <WinTheDayHero
            tallyData={tallyData}
            wtdData={wtdData}
            mtdData={mtdData}
            settings={settings}
            storeId={(typeof window !== "undefined" && window.__wowActiveStoreId) || user?.home_store_id}
            todayFocus={zoneSheet?.store_focus || ""}
            dateLabel={formatDateUS(selectedDate)}
          />
          {/* TodaysGamePlanStrip is part of the hero unit — it carries
              Focus + Contest + next Event chips so the manager sees
              the day's framing without a second scroll. */}
          <TodaysGamePlanStrip
            todayFocus={zoneSheet?.store_focus || ""}
            contestSummary={contestSummary}
            nextEvent={nextEvent}
            onGoToZoneSheet={() => {
              if (typeof onNavigate === "function") onNavigate("zone-sheet");
            }}
            onGoToContests={() => {
              if (typeof onNavigate === "function") onNavigate("contest");
            }}
            onGoToCalendar={() => {
              if (typeof onNavigate === "function") onNavigate("calendar");
            }}
          />
        </div>

        {/* ── 3. Store Pulse ──────────────────────────────────────── */}
        <SectionHeader
          title="Store Pulse"
          testid="section-store-pulse"
        />
        <div className="space-y-3">
          <EmailCaptureGlanceCard
            tallyData={tallyData}
            settings={settings}
          />
          {/* Pace to Win — 2026-02 Sales Recovery ask. Sandwiched between
              "here's where we are" (EmailCaptureGlanceCard) and "here's
              how to hit the goal" (GoalCalculator) so the flow reads:
              status → recovery guidance → capture math. Data comes from
              the same `/api/tally/{date}` poll — no new fetches. Comp
              Goal gate CTA opens Eyes on the Biz (Zone Sheet) where
              plan_sales + comp_goal_sales are entered. */}
          <PaceToWinCard
            tallyData={tallyData}
            onEditBizDay={() => {
              if (typeof onNavigate === "function") onNavigate("zone-sheet");
            }}
          />
          <div className="dashboard-card" data-testid="goal-calculator-card">
            <GoalCalculator tallyData={tallyData} settings={settings} />
          </div>
          <GoalStreak />
        </div>

        {/* ── 4. Game Plan ────────────────────────────────────────── */}
        <SectionHeader
          title="Game Plan"
          testid="section-game-plan"
        />
        <div className="space-y-3">
          <WhosOnTheFloor
            crewAssignments={zoneSheet?.crew_assignments || []}
            summary={coverageSummary}
            onGoToZoneSheet={() => {
              if (typeof onNavigate === "function") onNavigate("zone-sheet");
            }}
          />
          {/* WTD Pending Verification tray — surfaces every WTD row a
              crew member has signed off but that still needs the
              manager's OK (photos attached OR flagged as missing).
              Hidden entirely when nothing is waiting. (2026-08-30) */}
          <WtdPendingVerificationTray
            zoneSheet={zoneSheet}
            date={selectedDate}
            storeId={(typeof window !== "undefined" && window.__wowActiveStoreId) || user?.home_store_id || ""}
            actorId={user?.crew_id}
            crew={crew}
            onRefresh={async () => {
              const sid = (typeof window !== "undefined" && window.__wowActiveStoreId) || user?.home_store_id || "";
              try {
                const r = await axios.get(`${API}/zone-sheets/${selectedDate}`, { params: { store_id: sid, actor_id: user?.crew_id } });
                setZoneSheet(r.data || null);
              } catch { /* no-op */ }
            }}
          />
          {/* 2026-02 layout pass — collapse the CoverageTasksLiveFeed to
              a single-row chip when the store has no tasks yet. The full
              live feed comes back the moment a task is added. Empty-check
              uses the same coverageSummary the parent already fetched
              (see setup at top of dashboard) so no new API call. */}
          {((coverageSummary?.items || []).length === 0) ? (
            <EmptyStateChip
              icon={ListChecks}
              tint="slate"
              label="Mission Control"
              message="No tasks assigned yet — add crew + coverage on Zone Sheet"
              onClick={() => {
                if (typeof onNavigate === "function") onNavigate("zone-sheet");
              }}
              testid="tasks-empty-chip"
            />
          ) : (
            <CoverageTasksLiveFeed
              actorId={user.crew_id}
              storeId={(typeof window !== "undefined" && window.__wowActiveStoreId) || user.home_store_id}
              onOpenZoneSheet={() => {
                if (typeof window !== "undefined") window.location.href = "/?tab=zone-sheet";
              }}
            />
          )}
        </div>

        {/* ── 5. Manager Communication ────────────────────────────── */}
        <SectionHeader
          title="Manager Communication"
          testid="section-manager-comm"
        />
        <ManagerHandoffCard
          notes={zoneSheet?.manager_notes || ""}
          updatedById={zoneSheet?.manager_notes_updated_by}
          updatedAt={zoneSheet?.manager_notes_updated_at}
          authorName={(() => {
            const cid = zoneSheet?.manager_notes_updated_by;
            if (!cid) return "";
            const c = (crew || []).find((x) => x.id === cid);
            return c?.first_name || "";
          })()}
          lastShiftNotes={lastShiftNotes}
          lastShiftAuthorName={(() => {
            const cid = lastShiftNotes?.manager_notes_updated_by;
            if (!cid) return "";
            const c = (crew || []).find((x) => x.id === cid);
            return c?.first_name || "";
          })()}
          onEditOnZoneSheet={() => {
            if (typeof onNavigate === "function") {
              try {
                sessionStorage.setItem(
                  "wow:openSection",
                  JSON.stringify({ id: "manager-notes", date: selectedDate }),
                );
              } catch { /* quota */ }
              onNavigate("zone-sheet");
            }
          }}
        />

        {/* ── 6. Backroom & Freight ──────────────────────────────────
            Combined section (2026-08-31, user ask): pallets received =
            what needs to be worked in the backroom, so the truck plan
            and the live backroom counter belong side-by-side, not two
            sections apart. Freight Progress on top (real pallet count
            + received/cleared/target for today), Truck Plan below
            (the 7-day expected schedule with per-day received tally). */}
        <SectionHeader
          title="Backroom & Freight"
          testid="section-backroom-freight"
        />
        <div className="space-y-3">
          <FreightProgressCard
            storeId={(typeof window !== "undefined" && window.__wowActiveStoreId) || user?.home_store_id || ""}
            date={selectedDate}
            user={user}
          />
          <TruckPlanCard
          truckPlan={zoneSheet?.truck_plan || null}
          authorName={(() => {
            const cid = zoneSheet?.truck_plan?.updated_by;
            if (!cid) return "";
            const c = (crew || []).find((x) => x.id === cid);
            return c?.first_name || "";
          })()}
          onSave={async (next) => {
            try {
              const sid = (typeof window !== "undefined" && window.__wowActiveStoreId) || user?.home_store_id || "";
              await axios.patch(
                `${API}/zone-sheets/${selectedDate}`,
                { actor_id: user?.crew_id, truck_plan: next },
                { params: { store_id: sid } },
              );
              const r = await axios.get(`${API}/zone-sheets/${selectedDate}`, { params: { store_id: sid, actor_id: user?.crew_id } });
              setZoneSheet(r.data || null);
            } catch (e) {
              console.error("truck_plan save", e);
            }
          }}
        />
        </div>

        {/* ── 7. Recognition & Performance ────────────────────────── */}
        <SectionHeader
          title="Recognition & Performance"
          testid="section-recognition"
        />
        <div className="space-y-3">
          <CrewEmailAdjust
            crew={(() => {
              // Belt + braces over the server-side /crew-with-pins
              // store_id filter (added 2026-05-22). Even if apiScope
              // is briefly stale during a mirror toggle, this client
              // filter guarantees the leaderboard never lists
              // out-of-store crew. Also drops DMs from the
              // leaderboard — the DM is on staff but doesn't capture
              // emails on the sales floor of any single store.
              const scope = (typeof window !== "undefined" && window.__wowActiveStoreId) || user?.home_store_id || "";
              return crew.filter((c) => {
                if (!c.active) return false;
                if (c.is_district_manager) return false;
                if (!scope) return true;
                const cs = c.home_store_id || "";
                if (scope === "store_3094") return !cs || cs === "store_3094";
                return cs === scope;
              });
            })()}
            tallyData={tallyData}
            onGoToZoneSheet={() => {
              if (typeof onNavigate === "function") onNavigate("zone-sheet");
            }}
          />
        </div>

        {/* Reorderable tile grid (Today / Week / Contests) — lives
            INSIDE Recognition & Performance per the new sectioned
            layout. Tiles extracted out of this grid into dedicated
            sections above: goal_calc → Store Pulse, coverage_feed →
            Game Plan, crew_adjust → Recognition standalone. The
            remaining tiles are the "deeper data" widgets a manager
            opens less often. */}
        {(() => {
          const storageKey = `mgr_tiles_v2_${user?.employee_id || "default"}`;
          const tabKey = `mgr_tab_${user?.employee_id || "default"}`;
          // `biz_preview` (Eyes on the Biz) AND `weekly_figures` tiles
          // removed from Manager Dashboard 2026-02-15 — moved entirely
          // to Zone Sheet's "EYES ON THE BIZ" section so the entry +
          // review of weekly KPIs lives in one place.
          const defaultOrder = ["goal_calc", "crew_adjust", "wtd_financials", "wtd", "notes_history", "contests"];

          // Tile defs — extracted tiles (goal_calc, coverage_feed,
          // crew_adjust) render standalone in their dashboard sections
          // and are intentionally absent from this registry. See the
          // DEFAULT_TILE_ORDER block above for the migration note.
          const tileDefs = {
            contests: <ContestsAdmin user={user} />,
            wtd: <WTDSummary wtdData={wtdData} crew={crew} />,
            // Financial WTD — relocated from Zone Sheet to Manager
            // Dashboard 2026-06-12 per user privacy ask. WTD Sales
            // Plan / Comp / LY / +/- columns are confidential store
            // performance data so this tile is gated to managers
            // (gating enforced inside EyesOnBizPreview via `user`).
            wtd_financials: (
              <div className="relative rounded-3xl bg-white border border-slate-200/70 shadow-md ring-1 ring-slate-100 overflow-hidden" data-testid="wtd-financials-tile">
                <div className="absolute inset-x-0 top-0 h-1 pointer-events-none" style={{ background: "linear-gradient(90deg, #14b8a6 0%, #10b981 50%, #84cc16 100%)" }} />
                <div className="flex items-center gap-2.5 px-4 sm:px-5 pt-4 pb-3 border-b border-slate-100">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-2xl text-white shadow-md shrink-0" style={{ background: "linear-gradient(135deg, #10b981, #14b8a6)" }}>
                    <span className="font-black text-base">$</span>
                  </span>
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.22em] font-black text-emerald-600 leading-none">
                      Mission Control
                    </p>
                    <h3 className="text-lg font-black text-slate-900 leading-tight mt-1" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
                      WTD Financials
                    </h3>
                    <p className="text-[11px] text-slate-500 font-bold mt-0.5">Manager-only · auto-rolls daily checkpoint entries</p>
                  </div>
                </div>
                <div className="px-4 sm:px-5 py-4">
                  <EyesOnBizPreview
                    date={selectedDate}
                    refreshKey={bizRefreshKey}
                    hideHeader={true}
                    showToday={false}
                    showWTD={true}
                    user={user}
                  />
                </div>
              </div>
            ),
            notes_history: (
              <div className="max-w-2xl">
                <WeeklySummary />
              </div>
            ),
          };

          // Tab grouping — extracted tiles removed (see DEFAULT_TILE_ORDER
          // note). Today tab is empty by default after the reorg; it
          // remains in the pillbar for future "today-only deep dives"
          // a manager may opt into via Customize layout.
          const TILE_TABS = {
            wtd_financials: "week",
            wtd: "week",
            notes_history: "week",
            contests: "contests",
          };
          const TABS = [
            { id: "today",    label: "Today",    icon: ChartLine },
            { id: "week",     label: "Week",     icon: ChartLineUp },
            { id: "contests", label: "Contests", icon: Trophy },
          ];

          // Only keep tiles whose content exists
          const visibleOrder = tileOrder.filter((id) => tileDefs[id]);
          const tabFor = (id) => TILE_TABS[id] || "today";
          const tilesForTab = visibleOrder.filter((id) => tabFor(id) === activeTab);

          const move = (id, delta) => {
            // Reorder only within the same tab — slice out same-tab tiles,
            // swap there, then write the result back to the global order.
            const sameTab = visibleOrder.filter((tid) => tabFor(tid) === tabFor(id));
            const localIdx = sameTab.indexOf(id);
            const newLocalIdx = localIdx + delta;
            if (localIdx < 0 || newLocalIdx < 0 || newLocalIdx >= sameTab.length) return;
            const next = [...sameTab];
            [next[localIdx], next[newLocalIdx]] = [next[newLocalIdx], next[localIdx]];
            // Splice the new same-tab order back into the full list.
            const otherTabs = tileOrder.filter((tid) => tabFor(tid) !== tabFor(id));
            const merged = [];
            let sameIdx = 0;
            for (const tid of [...next, ...otherTabs]) {
              merged.push(tid);
              sameIdx += 1;
              if (sameIdx > 999) break;
            }
            // Simpler: keep otherTabs order intact, replace sameTab entries.
            const finalOrder = [];
            let i = 0;
            for (const tid of tileOrder) {
              if (tabFor(tid) === tabFor(id)) {
                finalOrder.push(next[i]);
                i += 1;
              } else {
                finalOrder.push(tid);
              }
            }
            setTileOrder(finalOrder);
            try { localStorage.setItem(storageKey, JSON.stringify(finalOrder)); } catch (e) { /* ignore quota */ }
          };

          const resetOrder = () => {
            setTileOrder(defaultOrder);
            try { localStorage.removeItem(storageKey); } catch (e) { /* ignore */ }
            toast.success("Layout reset");
          };

          const switchTab = (tab) => {
            setActiveTab(tab);
            try { localStorage.setItem(tabKey, tab); } catch (e) { /* ignore */ }
          };

          // Expose switchTab to the global Weekly Figures banner. Re-bound
          // every render so React state stays the source of truth.
          // (2026-05-25 fix for "red banner isn't clickable".)
          if (typeof window !== "undefined") {
            window.__wowSwitchTab = switchTab;
          }

          return (
            <div data-testid="reorderable-zone">
              {/* Tabs row — pill-shaped with FB-blue → purple gradient
                  active state. Renamed Today/Week/Contests labels
                  unchanged (they're navigation labels per user spec
                  "do not rename"); only the visual chrome refreshed. */}
              <div
                className="flex items-center gap-1.5 mb-4 p-1.5 rounded-full bg-white shadow-md ring-1 ring-slate-200 overflow-x-auto"
                role="tablist"
                data-testid="dashboard-tabs"
              >
                {TABS.map((t) => {
                  const Icon = t.icon;
                  const isActive = activeTab === t.id;
                  const tabTileCount = visibleOrder.filter((id) => tabFor(id) === t.id).length;
                  return (
                    <button
                      key={t.id}
                      onClick={() => switchTab(t.id)}
                      role="tab"
                      aria-selected={isActive}
                      className={`flex-1 inline-flex items-center justify-center gap-2 px-3.5 py-2.5 rounded-full text-sm font-black transition-all whitespace-nowrap ${
                        isActive
                          ? "text-white shadow-lg ring-1 ring-white/40"
                          : "text-slate-600 hover:bg-slate-50"
                      }`}
                      style={isActive ? { background: "linear-gradient(135deg, #0066FF 0%, #6366f1 50%, #a855f7 100%)" } : {}}
                      data-testid={`dashboard-tab-${t.id}`}
                    >
                      <Icon weight="fill" className="w-4 h-4" />
                      <span style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>{t.label}</span>
                      {tabTileCount > 0 && (
                        <span className={`text-[10px] font-black tabular-nums px-1.5 py-0.5 rounded-full ${isActive ? "bg-white/25 text-white" : "bg-slate-100 text-slate-500"}`}>
                          {tabTileCount}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Layout edit controls — restored 2026-06-14 alongside
                  the dashboard reorganization. Two states:
                    - editingLayout = false → small "Customize" button
                      (right of the tab pillbar). Discoverable but
                      unobtrusive. Flips the localStorage flag too so
                      the state persists across reloads.
                    - editingLayout = true  → Done + Reset controls
                      (same as before; Done clears the flag).
                  Profile → Personal Preferences → "Edit dashboard
                  layout" still works as a secondary entry. */}
              <div className="flex items-center justify-end gap-2 mb-3">
                {editingLayout ? (
                  <>
                    <button
                      onClick={resetOrder}
                      className="text-xs text-slate-500 hover:text-slate-700 underline"
                      data-testid="reset-layout-button"
                    >
                      Reset to default
                    </button>
                    <button
                      onClick={() => {
                        setEditingLayout(false);
                        try { localStorage.removeItem("wow_edit_layout"); } catch { /* quota */ }
                      }}
                      className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md text-white transition-colors"
                      style={{ backgroundColor: "var(--fb-blue)" }}
                      data-testid="edit-layout-button"
                    >
                      <DotsSixVertical weight="bold" className="w-3.5 h-3.5" />
                      Done
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      setEditingLayout(true);
                      try { localStorage.setItem("wow_edit_layout", "1"); } catch { /* quota */ }
                    }}
                    className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-md text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                    data-testid="customize-layout-button"
                  >
                    <DotsSixVertical weight="bold" className="w-3 h-3" />
                    Customize
                  </button>
                )}
              </div>

              <div className="space-y-6">
                {tilesForTab.length === 0 ? (
                  <div className="dashboard-card text-center py-10 text-slate-400" data-testid="dashboard-tab-empty">
                    Nothing here yet.
                  </div>
                ) : (
                  tilesForTab.map((id, idx) => (
                    <ReorderableTile
                      key={id}
                      id={id}
                      index={idx}
                      total={tilesForTab.length}
                      editing={editingLayout}
                      move={move}
                      testid={`tile-${id}`}
                    >
                      {tileDefs[id]}
                    </ReorderableTile>
                  ))
                )}
              </div>
            </div>
          );
        })()}

        {/* What's Fresh Today — small interim strip using EXISTING
            timestamps from today's zone sheet. Not an audit log
            (deferred to a later wave per user 2026-06-13); renders
            nothing if there's nothing fresh to show. Placed at the
            tail of the page so a manager scanning back at end of
            shift can see the cadence of activity without scrolling
            back to the Weekly Summary tile. */}
        <div className="mt-6">
          <WhatsFreshTodayStrip
            managerNotesUpdatedAt={zoneSheet?.manager_notes_updated_at}
            managerNotesAuthor={(() => {
              const cid = zoneSheet?.manager_notes_updated_by;
              if (!cid) return "";
              const c = (crew || []).find((x) => x.id === cid);
              return c?.first_name || "";
            })()}
            storeFocusUpdatedAt={zoneSheet?.store_focus_updated_at}
            storeFocusAuthor={(() => {
              const cid = zoneSheet?.store_focus_updated_by;
              if (!cid) return "";
              const c = (crew || []).find((x) => x.id === cid);
              return c?.first_name || "";
            })()}
            summary={coverageSummary}
          />
        </div>

        {/* Wow Crew Management moved to Settings page */}
      </main>
    </div>
  );
}
